import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Paper,
  Alert,
  Collapse,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress as MuiCircularProgress,
  Drawer,
  ListItemIcon,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapIcon,
  SkipNext as SkipIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FitnessCenter as FitnessIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  SwapVert as ReorderIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Timer as TimerIcon,
  FormatListNumbered as ListIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  RadioButtonUnchecked as PendingIcon,
  DragIndicator as DragHandleIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TrainingProgram,
  WorkoutSession,
  ProgramExercise,
  Exercise,
  CreateExerciseInput,
  CreateSetInput,
  PendingPlanProgram,
  Gym,
  WorkoutAdaptationContext,
  WorkoutAdaptationResponse,
  WorkoutCheckInPayload,
  TRAINING_LABEL_OPTIONS,
  TrainingLabel,
  ExerciseHistoryEntry,
  WORKOUT_SESSION_NOTES_MAX_LENGTH,
} from '../../services/types';
import { TrainingProgramService } from '../../services/trainingProgramService';
import { ProgramExerciseService } from '../../services/programExerciseService';
import { WorkoutSessionService } from '../../services/workoutSessionService';
import { ExerciseService } from '../../services/exerciseService';
import { SetService } from '../../services/setService';
import { WorkoutPlanService } from '../../services/workoutPlanService';
import { RotationalTrainingService } from '../../services/rotationalTrainingService';
import { GymService } from '../../services/gymService';
import { WorkoutAdaptationService } from '../../services/workoutAdaptationService';
import { WarmupService } from '../../services/warmupService';
import type { WarmupItem } from '../../services/types';
import { getImageUrl } from '../../services/api';
import { sanitizedHtmlProps } from '../../utils/sanitizeHtml';
import CardioSessionLogger from '../CardioSessionLogger';
import ExerciseMuscleMap from '../ExerciseMuscleMap';
import RestTimer from '../RestTimer';
import WorkoutTimer, { TimerState, TIMER_STORAGE_KEY } from '../WorkoutTimer';
import { getWorkoutStretchingContent } from '../../config/workoutStretchingContent';

const SESSION_KEY = 'gym-assistant-active-workout';
const WORKOUT_INTRO_DISABLED_KEY = 'gym-assistant-workout-intro-disabled';
const WORKOUT_INTRO_COUNTDOWN_STEPS = ['3', '2', '1', "Let's Start!"] as const;
const WORKOUT_INTRO_COUNTDOWN_DURATIONS = [1400, 1400, 1400, 1200] as const;
const WORKOUT_SESSION_UPDATED_EVENT = 'workout-session-updated';

type WorkoutIntroPhase = 'countdown' | 'warmup' | 'ready' | 'interactive-ask' | 'interactive-rest-pick' | 'complete';

interface WarmupGuideSlide {
  title: string;
  description: string;
  imageUrl: string | null;
}

const getTrustedIntroImageSrc = (url: string): string | null => {
  if (url.startsWith('/assets/')) {
    return url;
  }

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
};

const WARMUP_GUIDE_SLIDES: WarmupGuideSlide[] = [
  {
    title: 'Активирай ставите',
    description: 'Започни с 2-3 минути кръгове за рамене, таз и глезени, за да отпуснеш движението.',
    imageUrl: getTrustedIntroImageSrc('/assets/workout-intro/warmup-mobility.svg'),
  },
  {
    title: 'Вдигни пулса леко',
    description: 'Добави кратък динамичен блок с подскоци, марш на място или леко гребане.',
    imageUrl: getTrustedIntroImageSrc('/assets/workout-intro/warmup-cardio.svg'),
  },
  {
    title: 'Подготви първото движение',
    description: 'Направи 1-2 загряващи серии с лека тежест преди работните серии.',
    imageUrl: getTrustedIntroImageSrc('/assets/workout-intro/warmup-barbell.svg'),
  },
];

interface PersistedWorkout {
  workoutType: 'strength' | 'cardio';
  selectedProgram: TrainingProgram;
  selectedDay: number;
  exerciseLogs: ExerciseLog[];
  currentSession: WorkoutSession;
  currentStepIndex: number;
  programDays: { [key: number]: ProgramExercise[] };
  dayNames: { [key: number]: string };
  timerState?: TimerState | null;
  workoutStartedAt?: string | null;
  selectedGymId?: number | null;
  selectedTrainingLabel?: TrainingLabel | null;
  introCompleted?: boolean;
  interactiveMode?: boolean;
  interactiveRestSeconds?: number;
  interactivePosition?: { exerciseOffset: number; setIndex: number };
  interactiveSetPhase?: 'ready' | 'executing' | 'resting' | 'exercise-summary';
}

interface SetInput {
  setNumber: string; // "1", "2", "3.1", "3.2" for drop sets
  reps: number;
  repsLeft?: number;
  repsRight?: number;
  weight: number | string;
  assistance: boolean;
  rpe: number;
  isDropSet: boolean;
  dropSetOrder?: number;
  parentSetIndex?: number;
  notes?: string;
  durationSeconds?: number;
  restSeconds?: number;
  completed?: boolean;
  // Planned targets (populated when workout started from a plan)
  plannedReps?: number;
  plannedWeight?: number;
  plannedRpe?: number;
}

interface ExerciseLog {
  programExercise: ProgramExercise;
  sets: SetInput[];
  skipped: boolean;
  substitutedWith?: Exercise;
  lastWorkoutSummary?: string;
  supersetGroup?: string | null;
  unilateralOverride?: boolean;
}

type WorkoutStep = { exerciseIndices: number[]; isSuperSet: boolean };

interface WorkoutLoggerProps {
  initialProgramId?: number;
  initialDay?: number;
  onNavigateToPlanner?: () => void;
}

type WorkoutCompletionOutroStep = 'summary' | 'stretching';

const isActiveWorkoutSession = (session: WorkoutSession | null | undefined): boolean =>
  !!session && session.status !== 'completed' && session.status !== 'skipped';

const formatTimerDisplay = (totalSeconds: number): string => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

interface SortableExerciseItemProps {
  id: number;
  stepIdx: number;
  exerciseName: string;
  totalSets: number;
  doneSets: number;
  isCurrent: boolean;
  isCompleted: boolean;
  onSelect?: (stepIdx: number) => void;
}

function SortableExerciseItem({ id, stepIdx, exerciseName, totalSets, doneSets, isCurrent, isCompleted, onSelect }: SortableExerciseItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
      sx={{ minHeight: 56 }}
    >
      <Box
        {...listeners}
        {...attributes}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          minHeight: 56,
          cursor: 'grab',
          touchAction: 'none',
          color: 'text.secondary',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      {onSelect ? (
        <ListItemButton
          selected={isCurrent}
          onClick={() => onSelect(stepIdx)}
          sx={{ borderRadius: 1, pl: 0.5, minHeight: 56 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            {isCompleted ? (
              <CheckCircleIcon color="success" />
            ) : isCurrent ? (
              <PlayIcon color="primary" />
            ) : (
              <PendingIcon color="disabled" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={`${stepIdx + 1}. ${exerciseName}`}
            primaryTypographyProps={{ fontWeight: isCurrent ? 'bold' : 'normal' }}
          />
          <Chip
            label={`${doneSets}/${totalSets}`}
            size="small"
            color={isCompleted ? 'success' : doneSets > 0 ? 'warning' : 'default'}
            variant="outlined"
          />
        </ListItemButton>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, pl: 0.5, pr: 1, minHeight: 56 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {isCompleted ? (
              <CheckCircleIcon color="success" />
            ) : isCurrent ? (
              <PlayIcon color="primary" />
            ) : (
              <PendingIcon color="disabled" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={`${stepIdx + 1}. ${exerciseName}`}
            primaryTypographyProps={{ fontWeight: isCurrent ? 'bold' : 'normal' }}
          />
          <Chip
            label={`${doneSets}/${totalSets}`}
            size="small"
            color={isCompleted ? 'success' : doneSets > 0 ? 'warning' : 'default'}
            variant="outlined"
          />
        </Box>
      )}
    </ListItem>
  );
}

const WorkoutLogger: React.FC<WorkoutLoggerProps> = ({ initialProgramId, initialDay, onNavigateToPlanner }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [workoutType, setWorkoutType] = useState<'strength' | 'cardio' | null>(null); // NEW

  const [programs, setPrograms] = useState<TrainingProgram[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<TrainingProgram | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [programDays, setProgramDays] = useState<{ [key: number]: ProgramExercise[] }>({});
  const [dayNames, setDayNames] = useState<{ [key: number]: string }>({});
  const [exerciseLogs, setExerciseLogs] = useState<ExerciseLog[]>([]);
  const [currentSession, setCurrentSession] = useState<WorkoutSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [substituteDialogOpen, setSubstituteDialogOpen] = useState(false);
  const [addExerciseDialogOpen, setAddExerciseDialogOpen] = useState(false);
  const [substituteExerciseIndex, setSubstituteExerciseIndex] = useState<number | null>(null);
  const [availableExercises, setAvailableExercises] = useState<Exercise[]>([]);
  const [selectedSubstitute, setSelectedSubstitute] = useState<Exercise | null>(null);
  const [substituteEquipmentOptions, setSubstituteEquipmentOptions] = useState<string[]>([]);
  const [substituteMovementPatterns, setSubstituteMovementPatterns] = useState<string[]>([]);
  const [substituteMuscleGroups, setSubstituteMuscleGroups] = useState<string[]>([]);
  const [substituteFilterEquipment, setSubstituteFilterEquipment] = useState<string>('');
  const [substituteFilterMovementPattern, setSubstituteFilterMovementPattern] = useState<string>('');
  const [substituteHistory, setSubstituteHistory] = useState<ExerciseHistoryEntry[]>([]);
  const [substituteLoading, setSubstituteLoading] = useState(false);

  // Gym selection
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [selectedGymId, setSelectedGymId] = useState<number | null>(null);
  const [defaultGymId, setDefaultGymId] = useState<number | null>(null);
  const [savingDefaultGym, setSavingDefaultGym] = useState(false);
  const [sessionGymEquipmentIds, setSessionGymEquipmentIds] = useState<Set<number>>(new Set());
  const [selectedTrainingLabel, setSelectedTrainingLabel] = useState<TrainingLabel | null>(null);

  // Pending plan status for showing indicators
  const [pendingStatus, setPendingStatus] = useState<PendingPlanProgram | null>(null);

  // Substitute dialog search/filter state
  const [substituteSearch, setSubstituteSearch] = useState('');
  const [substituteFilterMuscle, setSubstituteFilterMuscle] = useState<string>('');

  // Quick-create exercise in substitute dialog
  const [showCreateExercise, setShowCreateExercise] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseMuscleGroup, setNewExerciseMuscleGroup] = useState('');
  const [newExerciseCategory, setNewExerciseCategory] = useState('');
  const [creatingExercise, setCreatingExercise] = useState(false);

  // Single set entry mode
  const [currentSetIndices, setCurrentSetIndices] = useState<Record<number, number>>({});
  const [expandedCompletedSets, setExpandedCompletedSets] = useState<Record<string, boolean>>({});

  // Completed steps tracking for summary view
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Variation switcher state
  const [variationDialogOpen, setVariationDialogOpen] = useState(false);
  const [variationOptions, setVariationOptions] = useState<Exercise[]>([]);
  // Interchangeable (swappable) alternatives for the current exercise, shown
  // after the variations in the variation-switcher dialog.
  const [alternativeOptions, setAlternativeOptions] = useState<Exercise[]>([]);
  const [loadingVariationOptions, setLoadingVariationOptions] = useState(false);

  // Step-by-step mode state
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showDetailsMap, setShowDetailsMap] = useState<Record<number, boolean>>({});
  const [variationExerciseIndex, setVariationExerciseIndex] = useState<number>(0);

  // Unplanned workout confirmation dialog state
  const [confirmUnplannedOpen, setConfirmUnplannedOpen] = useState(false);
  const [pendingDay, setPendingDay] = useState<number | null>(null);
  const [pendingPlanSessionId, setPendingPlanSessionId] = useState<number | null>(null);

  // Check-in and adaptation state
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [adaptationContext, setAdaptationContext] = useState<WorkoutAdaptationContext | null>(null);
  const [checkInData, setCheckInData] = useState<WorkoutCheckInPayload>({
    availableTimeMinutes: 60,
    fatigueLevel: 5,
    hasPain: false,
    painZone: '',
    sleepQuality: 7,
    goalFocus: 'performance',
    missingEquipmentIds: [],
  });
  const [checkInErrors, setCheckInErrors] = useState<string[]>([]);
  const [adaptationResult, setAdaptationResult] = useState<WorkoutAdaptationResponse | null>(null);
  const [adaptationLoading, setAdaptationLoading] = useState(false);
  const [checkInStepIndex, setCheckInStepIndex] = useState(0);

  // Timer state for workout time tracking
  const [timerState, setTimerState] = useState<TimerState | null>(null);
  const [workoutStartedAt, setWorkoutStartedAt] = useState<string | null>(null);

  // Completion summary state
  const [completionSummary, setCompletionSummary] = useState<{
    durationMinutes: number;
    activeMinutes: number;
    pauseMinutes: number;
    exerciseCount: number;
    setCount: number;
  } | null>(null);
  const [completionOutroStep, setCompletionOutroStep] = useState<WorkoutCompletionOutroStep>('summary');

  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerImages, setImageViewerImages] = useState<string[]>([]);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);
  const [imageViewerTitle, setImageViewerTitle] = useState('');
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [introPhase, setIntroPhase] = useState<WorkoutIntroPhase>('complete');
  const [introCompleted, setIntroCompleted] = useState(true);
  const [introDisableChecked, setIntroDisableChecked] = useState(false);
  const [introCountdownIndex, setIntroCountdownIndex] = useState(0);
  const [warmupGuideIndex, setWarmupGuideIndex] = useState(0);
  const [warmupImageErrors, setWarmupImageErrors] = useState<Record<number, boolean>>({});
  const [warmupPlanItems, setWarmupPlanItems] = useState<WarmupItem[] | null>(null);
  const [warmupPlanLoading, setWarmupPlanLoading] = useState(false);
  const [warmupCheckedIndices, setWarmupCheckedIndices] = useState<Set<number>>(new Set());

  // Interactive workout mode state
  const [interactiveMode, setInteractiveMode] = useState(false);
  const [interactiveRestSeconds, setInteractiveRestSeconds] = useState(90);
  const [interactiveSetPhase, setInteractiveSetPhase] = useState<
    'ready' | 'executing' | 'resting' | 'exercise-summary'
  >('ready');
  const [interactiveSetStartedAt, setInteractiveSetStartedAt] = useState<number | null>(null);
  const [interactiveSetElapsed, setInteractiveSetElapsed] = useState(0);
  const [interactiveRestRemaining, setInteractiveRestRemaining] = useState(0);
  const [interactivePosition, setInteractivePosition] = useState<{
    exerciseOffset: number;
    setIndex: number;
  }>({ exerciseOffset: 0, setIndex: 0 });

  // Change 1: rest phase editing — track just-completed set
  const interactiveLastCompletedRef = useRef<{ exerciseIndex: number; setIndex: number } | null>(null);
  const [interactiveRestEditOpen, setInteractiveRestEditOpen] = useState(false);

  // Change 2: exercise navigation drawer
  const [exerciseDrawerOpen, setExerciseDrawerOpen] = useState(false);
  const [reorderDrawerOpen, setReorderDrawerOpen] = useState(false);

  // Change 3: quick save
  const [quickSaveOpen, setQuickSaveOpen] = useState(false);
  const [quickSaveMinutes, setQuickSaveMinutes] = useState(0);
  const [quickSaveSeconds, setQuickSaveSeconds] = useState(45);

  const workoutContentRef = useRef<HTMLDivElement | null>(null);
  const saveInProgressRef = useRef(false);

  const clearActiveWorkoutState = useCallback(() => {
    setCurrentSession(null);
    setExerciseLogs([]);
    setCurrentStepIndex(0);
    setCompletedSteps(new Set());
    setExpandedCompletedSets({});
    setShowDetailsMap({});
    setSelectedDay(null);
    setWorkoutStartedAt(null);
    setTimerState(null);
    setIntroCompleted(false);
    setIntroPhase('complete');
    setWarmupPlanItems(null);
    setWarmupCheckedIndices(new Set());
    setInteractiveMode(false);
    setInteractiveSetPhase('ready');
    setInteractivePosition({ exerciseOffset: 0, setIndex: 0 });
    setInteractiveSetStartedAt(null);
    setInteractiveSetElapsed(0);
    setInteractiveRestRemaining(0);
  }, []);

  const refreshPendingStatusForSelectedProgram = useCallback(async () => {
    if (!selectedProgram) {
      setPendingStatus(null);
      return;
    }

    try {
      const status = await WorkoutPlanService.getPendingStatus();
      const programStatus = status.programs.find((program) => program.programId === selectedProgram.id);
      setPendingStatus(programStatus || null);
    } catch {
      // Ignore background refresh failures
    }
  }, [selectedProgram]);

  const syncCurrentSessionStatus = useCallback(async (sessionId: number) => {
    try {
      const latestSession = await WorkoutSessionService.getById(sessionId);

      if (isActiveWorkoutSession(latestSession)) {
        setCurrentSession((previousSession) => {
          if (previousSession?.id !== latestSession.id) {
            return latestSession;
          }

          return {
            ...latestSession,
            notes: latestSession.notes ?? previousSession.notes ?? null,
          };
        });
        return;
      }

      clearActiveWorkoutState();
      void refreshPendingStatusForSelectedProgram();
    } catch {
      clearActiveWorkoutState();
      void refreshPendingStatusForSelectedProgram();
    }
  }, [clearActiveWorkoutState, refreshPendingStatusForSelectedProgram]);

  // Compute workout steps: group consecutive exercises with same supersetGroup
  const steps = useMemo<WorkoutStep[]>(() => {
    const result: WorkoutStep[] = [];
    let i = 0;
    while (i < exerciseLogs.length) {
      const group = exerciseLogs[i].supersetGroup;
      if (group) {
        const indices: number[] = [i];
        while (i + 1 < exerciseLogs.length && exerciseLogs[i + 1].supersetGroup === group) {
          i++;
          indices.push(i);
        }
        result.push({ exerciseIndices: indices, isSuperSet: indices.length > 1 });
      } else {
        result.push({ exerciseIndices: [i], isSuperSet: false });
      }
      i++;
    }
    return result;
  }, [exerciseLogs]);

  const getSetCompletionKey = (exerciseIndex: number, setIndex: number) => `${exerciseIndex}:${setIndex}`;
  const isSetCompleted = (set: SetInput) => set.completed === true;
  const countCompletedSets = (sets: SetInput[]) => sets.filter(isSetCompleted).length;

  // Restore active workout from sessionStorage on mount
  useEffect(() => {
    try {
      const introDisabled = localStorage.getItem(WORKOUT_INTRO_DISABLED_KEY) === 'true';
      setIntroDisableChecked(introDisabled);

      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) {
        const persisted: PersistedWorkout = JSON.parse(saved);
        if (!isActiveWorkoutSession(persisted.currentSession)) {
          sessionStorage.removeItem(SESSION_KEY);
          return;
        }

        setWorkoutType(persisted.workoutType);
        setSelectedProgram(persisted.selectedProgram);
        setSelectedDay(persisted.selectedDay);
        setExerciseLogs(persisted.exerciseLogs);
        setCurrentSession(persisted.currentSession);
        setCurrentStepIndex(persisted.currentStepIndex);
        setProgramDays(persisted.programDays);
        setDayNames(persisted.dayNames);
        if (persisted.timerState) {
          setTimerState(persisted.timerState);
          setWorkoutStartedAt(persisted.timerState.startedAt);
        } else if (persisted.workoutStartedAt) {
          setWorkoutStartedAt(persisted.workoutStartedAt);
        }
        if (persisted.selectedGymId !== undefined) {
          setSelectedGymId(persisted.selectedGymId ?? null);
        }
        if (persisted.selectedTrainingLabel !== undefined) {
          setSelectedTrainingLabel(persisted.selectedTrainingLabel ?? null);
        } else if (persisted.currentSession?.trainingLabel !== undefined) {
          setSelectedTrainingLabel(persisted.currentSession.trainingLabel ?? null);
        }
        if (persisted.introCompleted !== undefined) {
          const hasStartedLogging = persisted.exerciseLogs.length > 0 || !!persisted.workoutStartedAt || !!persisted.timerState?.startedAt;
          const shouldTreatIntroAsComplete = persisted.introCompleted || hasStartedLogging || introDisabled;
          setIntroCompleted(shouldTreatIntroAsComplete);
          setIntroPhase(shouldTreatIntroAsComplete ? 'complete' : 'countdown');
        } else if (persisted.workoutType === 'strength' && persisted.currentSession && !introDisabled) {
          const hasStartedLogging = persisted.exerciseLogs.length > 0 || !!persisted.workoutStartedAt || !!persisted.timerState?.startedAt;
          setIntroCompleted(hasStartedLogging);
          setIntroPhase(hasStartedLogging ? 'complete' : 'countdown');
        }

        // Restore interactive mode state
        if (persisted.interactiveMode) {
          setInteractiveMode(true);
          setInteractiveRestSeconds(persisted.interactiveRestSeconds ?? 90);
          setInteractivePosition(persisted.interactivePosition ?? { exerciseOffset: 0, setIndex: 0 });
          // Reset executing/resting to ready on restore (timer state is lost)
          const restoredPhase = persisted.interactiveSetPhase;
          setInteractiveSetPhase(
            restoredPhase === 'exercise-summary' ? 'exercise-summary' : 'ready'
          );
        }

        void syncCurrentSessionStatus(persisted.currentSession.id);
      }
    } catch (err) {
      console.error('Failed to restore workout from sessionStorage:', err);
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [syncCurrentSessionStatus]);

  useEffect(() => {
    loadPrograms();
    loadWorkoutGymSelector();
  }, []);

  useEffect(() => {
    if (!selectedGymId) {
      setSessionGymEquipmentIds(new Set());
      return;
    }
    GymService.getById(selectedGymId).then((gym) => {
      const ids = new Set<number>(
        (gym.equipment || [])
          .map((e) => {
            if (typeof e === 'string') {
              const match = e.match(/\/(\d+)$/);
              return match ? parseInt(match[1], 10) : null;
            }
            return typeof e === 'object' && e !== null ? (e as { id: number }).id : null;
          })
          .filter((id): id is number => id !== null)
      );
      setSessionGymEquipmentIds(ids);
    }).catch(() => {});
  }, [selectedGymId]);

  useEffect(() => {
    // If initial program and day are provided, auto-start workout
    if (initialProgramId && initialDay && programs.length > 0) {
      const program = programs.find((p) => p.id === initialProgramId);
      if (program) {
        handleProgramSelect(program).then(() => {
          handleDaySelect(initialDay);
        });
      }
    }
  }, [initialProgramId, initialDay, programs]);

  // Persist active workout to sessionStorage
  useEffect(() => {
    if (currentSession && selectedProgram && selectedDay !== null && workoutType) {
      const persisted: PersistedWorkout = {
        workoutType: workoutType as 'strength' | 'cardio',
        selectedProgram,
        selectedDay,
        exerciseLogs,
        currentSession,
        currentStepIndex,
        programDays,
        dayNames,
        timerState,
        workoutStartedAt,
        selectedGymId,
        selectedTrainingLabel,
        introCompleted,
        interactiveMode,
        interactiveRestSeconds,
        interactivePosition,
        interactiveSetPhase,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(persisted));
    } else if (!currentSession) {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [exerciseLogs, currentStepIndex, currentSession, selectedDay, workoutType, selectedProgram, programDays, dayNames, timerState, workoutStartedAt, selectedGymId, selectedTrainingLabel, introCompleted, interactiveMode, interactiveRestSeconds, interactivePosition, interactiveSetPhase]);

  useEffect(() => {
    const handleWorkoutSessionUpdated = () => {
      if (saveInProgressRef.current) {
        return;
      }

      if (!currentSession) {
        void refreshPendingStatusForSelectedProgram();
        return;
      }

      if (currentSession.status === 'completed') {
        return;
      }

      void syncCurrentSessionStatus(currentSession.id);
    };

    window.addEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleWorkoutSessionUpdated);

    return () => {
      window.removeEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleWorkoutSessionUpdated);
    };
  }, [currentSession, refreshPendingStatusForSelectedProgram, syncCurrentSessionStatus]);

  const startWorkoutIntro = useCallback(() => {
    const introDisabled = localStorage.getItem(WORKOUT_INTRO_DISABLED_KEY) === 'true';
    setIntroDisableChecked(introDisabled);
    setWarmupGuideIndex(0);
    setWarmupImageErrors({});
    setIntroCountdownIndex(0);

    if (introDisabled) {
      setIntroCompleted(true);
      setIntroPhase('complete');
      return;
    }

    setIntroCompleted(false);
    setIntroPhase('countdown');
  }, []);

  const finishWorkoutIntro = useCallback((disableForFuture: boolean) => {
    if (disableForFuture) {
      localStorage.setItem(WORKOUT_INTRO_DISABLED_KEY, 'true');
    }
    setIntroDisableChecked(disableForFuture || localStorage.getItem(WORKOUT_INTRO_DISABLED_KEY) === 'true');
    setIntroCompleted(true);
    setIntroPhase('complete');
  }, []);

  const handleWorkoutTypeSelect = useCallback((type: 'strength' | 'cardio') => {
    setWorkoutType(type);

    if (type === 'strength' && currentSession && !introCompleted) {
      startWorkoutIntro();
      return;
    }

    if (type !== 'strength') {
      setIntroPhase('complete');
    }
  }, [currentSession, introCompleted, startWorkoutIntro]);

  useEffect(() => {
    if (introPhase !== 'countdown') {
      return;
    }

    const timeout = window.setTimeout(() => {
      if (introCountdownIndex >= WORKOUT_INTRO_COUNTDOWN_STEPS.length - 1) {
        setIntroPhase('warmup');
        return;
      }

      setIntroCountdownIndex((prev) => prev + 1);
    }, WORKOUT_INTRO_COUNTDOWN_DURATIONS[introCountdownIndex]);

    return () => window.clearTimeout(timeout);
  }, [introCountdownIndex, introPhase]);

  // Load dynamic warmup plan when entering warmup phase
  useEffect(() => {
    if (introPhase !== 'warmup' || !currentSession?.id) {
      return;
    }

    let cancelled = false;
    setWarmupPlanLoading(true);
    setWarmupCheckedIndices(new Set());

    WarmupService.getWarmupPlan(currentSession.id)
      .then((plan) => {
        if (!cancelled && plan.items.length > 0) {
          setWarmupPlanItems(plan.items);
        }
      })
      .catch(() => {
        // Silently fall back to static slides — warmupPlanItems stays null
      })
      .finally(() => {
        if (!cancelled) {
          setWarmupPlanLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [introPhase, currentSession?.id]);

  // Interactive mode: count-up timer for set execution
  useEffect(() => {
    if (!interactiveMode || interactiveSetPhase !== 'executing' || !interactiveSetStartedAt) return;
    const interval = setInterval(() => {
      setInteractiveSetElapsed(Math.floor((Date.now() - interactiveSetStartedAt) / 1000));
    }, 200);
    return () => clearInterval(interval);
  }, [interactiveMode, interactiveSetPhase, interactiveSetStartedAt]);

  // Interactive mode: rest countdown timer
  useEffect(() => {
    if (!interactiveMode || interactiveSetPhase !== 'resting' || interactiveRestRemaining <= 0) return;
    const interval = setInterval(() => {
      setInteractiveRestRemaining((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [interactiveMode, interactiveSetPhase, interactiveRestRemaining]);

  // Interactive mode: auto-advance when rest finishes
  useEffect(() => {
    if (!interactiveMode || interactiveSetPhase !== 'resting' || interactiveRestRemaining !== 0) return;

    // Notification + vibration
    try {
      if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
      if (Notification.permission === 'granted') {
        new Notification('Почивката свърши!', { body: 'Време за следващата серия' });
      }
    } catch { /* ignore */ }

    // Advance to next set
    const step = steps[currentStepIndex];
    if (!step) return;
    const { exerciseOffset, setIndex } = interactivePosition;
    const nextOffset = exerciseOffset + 1;

    if (nextOffset < step.exerciseIndices.length) {
      setInteractivePosition({ exerciseOffset: nextOffset, setIndex });
      setInteractiveSetPhase('ready');
    } else {
      const nextSetIndex = setIndex + 1;
      const allDone = step.exerciseIndices.every((exIdx) =>
        nextSetIndex >= exerciseLogs[exIdx]?.sets.length
      );
      if (allDone) {
        setInteractiveSetPhase('exercise-summary');
      } else {
        setInteractivePosition({ exerciseOffset: 0, setIndex: nextSetIndex });
        setInteractiveSetPhase('ready');
      }
    }

    setInteractiveSetElapsed(0);
    setInteractiveSetStartedAt(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [interactiveMode, interactiveSetPhase, interactiveRestRemaining, steps, currentStepIndex, interactivePosition, exerciseLogs]);

  useEffect(() => {
    if (!workoutContentRef.current) {
      return;
    }

    if (introPhase !== 'complete') {
      workoutContentRef.current.setAttribute('inert', '');
      workoutContentRef.current.setAttribute('aria-hidden', 'true');
    } else {
      workoutContentRef.current.removeAttribute('inert');
      workoutContentRef.current.removeAttribute('aria-hidden');
    }
  }, [introPhase]);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const data = await TrainingProgramService.getAll();
      setPrograms(data.filter(p => p.active));
    } catch (err) {
      setError('Грешка при зареждане на програмите');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkoutGymSelector = async () => {
    try {
      const selectorData = await GymService.getWorkoutGymSelector();
      setGyms(selectorData.gyms);
      setDefaultGymId(selectorData.defaultGymId ?? null);
      setSelectedGymId((currentSelectedGymId) => {
        if (currentSelectedGymId && selectorData.gyms.some((gym) => gym.id === currentSelectedGymId)) {
          return currentSelectedGymId;
        }
        if (selectorData.defaultGymId && selectorData.gyms.some((gym) => gym.id === selectorData.defaultGymId)) {
          return selectorData.defaultGymId;
        }
        return null;
      });
    } catch (err) {
      console.error('Failed to load workout gym selector data:', err);
      // Fallback to current behavior if the new endpoint is unavailable
      GymService.getAll().then(setGyms).catch(() => {});
    }
  };

  const handleSetDefaultGym = async () => {
    if (!selectedGymId) {
      return;
    }

    try {
      setSavingDefaultGym(true);
      setError(null);
      const result = await GymService.setDefaultGym(selectedGymId);
      setDefaultGymId(result.defaultGymId ?? null);
    } catch (err: any) {
      if (err?.status === 403) {
        setError('Нямаш достъп до избраната зала.');
      } else if (err?.status === 422) {
        setError('Невалидна зала за default.');
      } else {
        setError('Грешка при задаване на default зала.');
      }
    } finally {
      setSavingDefaultGym(false);
    }
  };

  const handleProgramSelect = async (program: TrainingProgram) => {
    try {
      setLoading(true);
      setSelectedProgram(program);
      setSelectedDay(null);
      setExerciseLogs([]);

      const [response] = await Promise.all([
        ProgramExerciseService.getProgramExercises(program.id),
        WorkoutPlanService.getPendingStatus().then(status => {
          const programStatus = status.programs.find(p => p.programId === program.id);
          setPendingStatus(programStatus || null);
        }).catch(() => {}),
      ]);
      setProgramDays(response.exercisesByDay);
      setDayNames(response.dayNames || {});
    } catch (err) {
      setError('Грешка при зареждане на упражненията');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDaySelect = async (day: number) => {
    if (!selectedProgram) return;

    try {
      setLoading(true);

      // Check if there's an existing plan for this day
      const planResult = await WorkoutPlanService.findPlanForDay(selectedProgram.id, day);

      if (planResult.found && planResult.session) {
        // Plan exists - require check-in/adaptation review before final start.
        await openCheckInForPlannedSession(planResult.session.id, day);
      } else {
        // No plan - start an on-the-fly workout session directly.
        await startWorkoutWithoutPlan(day);
      }
    } catch (err) {
      setError('Грешка при стартиране на тренировката');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openCheckInForPlannedSession = async (sessionId: number, day: number) => {
    setPendingPlanSessionId(sessionId);
    setPendingDay(day);
    setCheckInStepIndex(0);
    setCheckInErrors([]);
    setAdaptationResult(null);
    setCheckInData((prev) => ({ ...prev, missingEquipmentIds: [], painZone: prev.hasPain ? prev.painZone : '' }));

    try {
      const context = await WorkoutAdaptationService.getContext(sessionId, selectedGymId);
      setAdaptationContext(context);
    } catch (err) {
      console.error('Failed to load adaptation context:', err);
      setAdaptationContext({
        skipEquipmentQuestion: true,
        sessionGymId: selectedGymId ?? null,
        defaultGymId,
        equipmentOptions: [],
      });
    }

    setCheckInDialogOpen(true);
  };

  const closeCheckInDialog = () => {
    setCheckInDialogOpen(false);
    setPendingPlanSessionId(null);
    setPendingDay(null);
    setCheckInStepIndex(0);
    setCheckInErrors([]);
    setAdaptationResult(null);
    setAdaptationLoading(false);
  };

  const shouldShowEquipmentQuestion = !adaptationContext?.skipEquipmentQuestion && Boolean(adaptationContext?.equipmentOptions?.length);

  const checkInSteps = useMemo(() => {
    const steps: Array<{
      key: 'availableTimeMinutes' | 'fatigueLevel' | 'hasPain' | 'painZone' | 'sleepQuality' | 'goalFocus' | 'missingEquipmentIds';
      title: string;
      description: string;
      icon: React.ReactNode;
    }> = [
      {
        key: 'availableTimeMinutes',
        title: 'Колко време имаш днес?',
        description: 'Ще използваме отговора, за да нагодим обема и темпото на сесията.',
        icon: <EventNoteIcon sx={{ fontSize: 42, color: 'primary.main' }} />,
      },
      {
        key: 'fatigueLevel',
        title: 'Как е енергията ти в момента?',
        description: 'Оцени общата умора по скала от 1 до 10.',
        icon: <FitnessIcon sx={{ fontSize: 42, color: 'primary.main' }} />,
      },
      {
        key: 'hasPain',
        title: 'Чувстваш ли болка?',
        description: 'Ако има болка, ще поискаме още един детайл преди старта.',
        icon: <InfoIcon sx={{ fontSize: 42, color: 'primary.main' }} />,
      },
      {
        key: 'sleepQuality',
        title: 'Как беше сънят ти?',
        description: 'Оцени качеството на съня по скала от 1 до 10.',
        icon: <CheckCircleIcon sx={{ fontSize: 42, color: 'primary.main' }} />,
      },
      {
        key: 'goalFocus',
        title: 'Какъв е фокусът ти днес?',
        description: 'Избираме приоритет за днешната тренировка.',
        icon: <SearchIcon sx={{ fontSize: 42, color: 'primary.main' }} />,
      },
    ];

    if (checkInData.hasPain) {
      steps.splice(3, 0, {
        key: 'painZone',
        title: 'Къде е болката?',
        description: 'Опиши кратко зоната, за да може адаптацията да реагира адекватно.',
        icon: <InfoIcon sx={{ fontSize: 42, color: 'warning.main' }} />,
      });
    }

    if (shouldShowEquipmentQuestion) {
      steps.push({
        key: 'missingEquipmentIds',
        title: 'Какво оборудване липсва?',
        description: 'Избери всичко, което не е налично в залата днес.',
        icon: <SwapIcon sx={{ fontSize: 42, color: 'primary.main' }} />,
      });
    }

    return steps;
  }, [checkInData.hasPain, shouldShowEquipmentQuestion]);

  useEffect(() => {
    setCheckInStepIndex((prev) => Math.min(prev, checkInSteps.length));
  }, [checkInSteps]);

  const validateCheckIn = (): string[] => {
    const errors: string[] = [];
    if (checkInData.availableTimeMinutes < 5 || checkInData.availableTimeMinutes > 240) {
      errors.push('Времето трябва да е между 5 и 240 минути');
    }
    if (checkInData.fatigueLevel < 1 || checkInData.fatigueLevel > 10) {
      errors.push('Умората трябва да е между 1 и 10');
    }
    if (checkInData.sleepQuality < 1 || checkInData.sleepQuality > 10) {
      errors.push('Сънят трябва да е между 1 и 10');
    }
    if (checkInData.hasPain && !(checkInData.painZone || '').trim()) {
      errors.push('Добави зона на болка');
    }

    return errors;
  };

  const validateCheckInStep = (stepKey: string): string[] => {
    switch (stepKey) {
      case 'availableTimeMinutes':
        return checkInData.availableTimeMinutes < 5 || checkInData.availableTimeMinutes > 240
          ? ['Времето трябва да е между 5 и 240 минути']
          : [];
      case 'fatigueLevel':
        return checkInData.fatigueLevel < 1 || checkInData.fatigueLevel > 10
          ? ['Умората трябва да е между 1 и 10']
          : [];
      case 'painZone':
        return checkInData.hasPain && !(checkInData.painZone || '').trim()
          ? ['Добави зона на болка']
          : [];
      case 'sleepQuality':
        return checkInData.sleepQuality < 1 || checkInData.sleepQuality > 10
          ? ['Сънят трябва да е между 1 и 10']
          : [];
      case 'goalFocus':
        return checkInData.goalFocus
          ? []
          : ['Избери фокус за тренировката'];
      default:
        return [];
    }
  };

  const handleSubmitCheckIn = async () => {
    if (!pendingPlanSessionId) return;

    const errors = validateCheckIn();
    setCheckInErrors(errors);
    if (errors.length > 0) return;

    try {
      setAdaptationLoading(true);
      setCheckInErrors([]);
      const result = await WorkoutAdaptationService.submitCheckIn(
        pendingPlanSessionId,
        checkInData
      );
      setAdaptationResult(result);
    } catch (err: any) {
      if (err?.status === 422) {
        setCheckInErrors(['Невалидни check-in данни. Провери полетата и опитай отново.']);
        setAdaptationResult(null);
        return;
      }

      setError('Грешка при адаптация. Тренировката ще стартира с оригиналния план.');
      setAdaptationResult({
        status: 'adaptation_not_applied',
        adaptationApplied: false,
        message: 'Адаптацията не беше приложена. Ще се стартира оригиналният план.',
        plan: {
          sessionId: pendingPlanSessionId!,
          sessionName: '',
          dayNumber: pendingDay ?? null,
          exercises: [],
        },
        appliedChanges: [],
        rejectedChanges: [],
      });
      console.error(err);
    } finally {
      setAdaptationLoading(false);
    }
  };

  const handleStartAfterCheckIn = async () => {
    if (pendingPlanSessionId === null || pendingDay === null || !adaptationResult) return;
    setCheckInDialogOpen(false);
    await startWorkoutFromPlan(pendingPlanSessionId, pendingDay, adaptationResult?.plan.exercises);
    setPendingPlanSessionId(null);
    setPendingDay(null);
    setAdaptationResult(null);
    setCheckInErrors([]);
  };

  const startWorkoutFromPlan = async (planSessionId: number, day: number, adaptedExercises?: WorkoutAdaptationResponse['plan']['exercises']) => {
    if (!selectedProgram) return;

    try {
      // Load plan details
      const plan = await WorkoutPlanService.getById(planSessionId);

      // Transition the planned session to in_progress, record startedAt and assign gym
      const startedAtIso = new Date().toISOString();
      const updateData: any = {
        status: 'in_progress',
        startedAt: startedAtIso,
        notes: null,
      };
      if (selectedGymId) {
        updateData.gym = `/api/gyms/${selectedGymId}`;
      }
      updateData.trainingLabel = selectedTrainingLabel;
      const session = await WorkoutSessionService.update(planSessionId, updateData);
      setCurrentSession(session);
      setSelectedDay(day);
      setWorkoutStartedAt(startedAtIso);
      setTimerState({
        startedAt: startedAtIso,
        isPaused: false,
        pausedAt: null,
        totalPausedSeconds: 0,
      });

      // Build exercise logs from the plan
      const dayExercises = programDays[day] || [];
      const logs: ExerciseLog[] = [];

      const sourceExercises = adaptedExercises?.length ? adaptedExercises.map((exercise) => ({
        exerciseId: exercise.exerciseId,
        exerciseName: exercise.exerciseName,
        muscleGroup: exercise.muscleGroup,
        isSubstitution: exercise.isSubstitution,
        sets: exercise.sets,
      })) : plan.exercises;

      for (const planExercise of sourceExercises) {
        // Find the matching program exercise (or create a stub)
        const matchingPE = dayExercises.find(pe => pe.exercise.id === planExercise.exerciseId);

        // Fetch last workout summary for display
        const lastWorkoutData = await ExerciseService.getLastWorkoutData(planExercise.exerciseId);

        const exerciseIsUnilateral = matchingPE?.exercise?.unilateral || false;
        const sets: SetInput[] = planExercise.sets.map(ps => ({
          setNumber: ps.setOrder.toString(),
          reps: ps.targetReps,
          ...(exerciseIsUnilateral ? { repsLeft: ps.targetReps, repsRight: ps.targetReps } : {}),
          weight: ps.targetWeight ?? 0,
          assistance: false,
          rpe: ps.targetRpe ?? 7,
          isDropSet: false,
          plannedReps: ps.targetReps,
          plannedWeight: ps.targetWeight ?? undefined,
          plannedRpe: ps.targetRpe ?? undefined,
        }));

        if (matchingPE) {
          logs.push({
            programExercise: matchingPE,
            sets,
            skipped: false,
            substitutedWith: planExercise.isSubstitution
              ? { id: planExercise.exerciseId, name: planExercise.exerciseName } as Exercise
              : undefined,
            lastWorkoutSummary: lastWorkoutData?.summary || undefined,
            supersetGroup: matchingPE.supersetGroup || null,
          });
        } else {
          // Exercise from plan that's not in the current program day - create a stub PE
          const stubPE: ProgramExercise = {
            id: 0,
            exercise: {
              id: planExercise.exerciseId,
              name: planExercise.exerciseName,
              muscleGroup: planExercise.muscleGroup,
              stimulusToFatigueRatio: '0.00',
              category: '',
              equipment: '',
              difficulty: '',
              description: '',
              createdAt: '',
            },
            dayOfWeek: day,
            orderInSession: logs.length,
            createdAt: '',
          };
          logs.push({
            programExercise: stubPE,
            sets,
            skipped: false,
            lastWorkoutSummary: lastWorkoutData?.summary || undefined,
            supersetGroup: null,
          });
        }
      }

      setExerciseLogs(logs);
      setCurrentStepIndex(0);
      setShowDetailsMap({});
      setIntroCompleted(true);
      setIntroPhase('complete');
    } catch (err) {
      setError('Грешка при зареждане на плана');
      console.error(err);
    }
  };

  const currentCheckInStep = checkInSteps[checkInStepIndex] ?? null;
  const isCheckInReviewStep = checkInStepIndex >= checkInSteps.length;
  const checkInProgressValue = checkInSteps.length === 0
    ? 0
    : (Math.min(checkInStepIndex + 1, checkInSteps.length) / checkInSteps.length) * 100;

  const handleCheckInNext = async () => {
    if (!currentCheckInStep) {
      return;
    }

    const stepErrors = validateCheckInStep(currentCheckInStep.key);
    setCheckInErrors(stepErrors);
    if (stepErrors.length > 0) {
      return;
    }

    if (checkInStepIndex === checkInSteps.length - 1) {
      const finalErrors = validateCheckIn();
      setCheckInErrors(finalErrors);
      if (finalErrors.length > 0) {
        return;
      }

      setCheckInStepIndex(checkInSteps.length);
      await handleSubmitCheckIn();
      return;
    }

    setCheckInErrors([]);
    setCheckInStepIndex((prev) => prev + 1);
  };

  const handleCheckInBack = () => {
    if (adaptationLoading) {
      return;
    }

    setCheckInErrors([]);
    setAdaptationResult(null);
    setCheckInStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const startWorkoutWithoutPlan = async (day: number) => {
    if (!selectedProgram) return;

    try {
      setLoading(true);
      setSelectedDay(day);

      // Create workout session with startedAt and optional gym
      const startedAtIso = new Date().toISOString();
      const sessionData: any = {
        name: `${selectedProgram.name} - ${dayNames[day] || `Day ${day}`}`,
        notes: null,
        workoutDate: new Date().toLocaleDateString('en-CA'),
        status: 'in_progress',
        startedAt: startedAtIso,
        trainingProgram: `/api/training_programs/${selectedProgram.id}`,
        programDayNumber: day,
      };
      if (selectedGymId) {
        sessionData.gym = `/api/gyms/${selectedGymId}`;
      }
      sessionData.trainingLabel = selectedTrainingLabel;
      const session = await WorkoutSessionService.create(sessionData);
      setCurrentSession(session);
      setWorkoutStartedAt(startedAtIso);
      setTimerState({
        startedAt: startedAtIso,
        isPaused: false,
        pausedAt: null,
        totalPausedSeconds: 0,
      });

      // Load exercises for the selected day and initialize logs
      const dayExercises = programDays[day] || [];
      const logs: ExerciseLog[] = await Promise.all(
        dayExercises.map(async (pe) => {
          const lastWorkoutData = await ExerciseService.getLastWorkoutData(pe.exercise.id);

          const sets: SetInput[] = [];
          const numSets = pe.targetSets || 3;
          for (let i = 1; i <= numSets; i++) {
            const lastWeightNum = lastWorkoutData?.sets?.[i - 1]?.weight;
            const lastWeight = typeof lastWeightNum === 'number' ? lastWeightNum : 0;

            sets.push({
              setNumber: i.toString(),
              reps: pe.targetReps || 10,
              ...(pe.exercise.unilateral ? { repsLeft: pe.targetReps || 10, repsRight: pe.targetReps || 10 } : {}),
              weight: lastWeight,
              assistance: false,
              rpe: 7,
              isDropSet: false,
            });
          }

          return {
            programExercise: pe,
            sets,
            skipped: false,
            lastWorkoutSummary: lastWorkoutData?.summary || undefined,
            supersetGroup: pe.supersetGroup || null,
          };
        })
      );

      setExerciseLogs(logs);
      setCurrentStepIndex(0);
      setShowDetailsMap({});
      setIntroCompleted(true);
      setIntroPhase('complete');
    } catch (err) {
      setError('Грешка при стартиране на тренировката');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetChange = (
    exerciseIndex: number,
    setIndex: number,
    field: keyof SetInput,
    value: any
  ) => {
    setExerciseLogs((prev) =>
      prev.map((log, i) =>
        i !== exerciseIndex ? log : {
          ...log,
          sets: log.sets.map((set, j) =>
            j !== setIndex ? set : { ...set, [field]: value }
          ),
        }
      )
    );
  };

  const handleSetCompletedChange = (exerciseIndex: number, setIndex: number, completed: boolean) => {
    handleSetChange(exerciseIndex, setIndex, 'completed', completed);
    const key = getSetCompletionKey(exerciseIndex, setIndex);
    setExpandedCompletedSets((prev) => {
      const next = { ...prev };
      if (completed) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const toggleCompletedSetExpanded = (exerciseIndex: number, setIndex: number) => {
    const key = getSetCompletionKey(exerciseIndex, setIndex);
    setExpandedCompletedSets((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSessionNotesChange = (value: string) => {
    if (value.length > WORKOUT_SESSION_NOTES_MAX_LENGTH) {
      return;
    }

    setCurrentSession((prev) => (
      prev ? { ...prev, notes: value } : prev
    ));

    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (!saved) {
        return;
      }

      const persisted: PersistedWorkout = JSON.parse(saved);
      if (!isActiveWorkoutSession(persisted.currentSession)) {
        return;
      }

      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          ...persisted,
          currentSession: {
            ...persisted.currentSession,
            notes: value,
          },
        })
      );
    } catch {
      // Best effort only; the normal persistence effect still handles valid state updates.
    }
  };

  const handleAddDropSet = (exerciseIndex: number, setIndex: number) => {
    setExerciseLogs((prev) => {
      const updated = [...prev];
      const currentLog = updated[exerciseIndex];
      const currentSet = currentLog.sets[setIndex];
      const baseSetNumber = currentSet.setNumber.split('.')[0];
      const currentExercise = currentLog.substitutedWith || currentLog.programExercise.exercise;
      const isUni = !!(currentExercise.unilateral || currentLog.unilateralOverride);

      // Find existing drop sets for this main set
      const existingDropSets = currentLog.sets.filter(
        (s) => s.parentSetIndex === setIndex
      );
      const nextDropSetOrder = existingDropSets.length + 1;

      // Create new drop set
      const newDropSet: SetInput = {
        setNumber: `${baseSetNumber}.${nextDropSetOrder + 1}`,
        reps: currentSet.reps,
        ...(isUni ? { repsLeft: currentSet.repsLeft ?? currentSet.reps, repsRight: currentSet.repsRight ?? currentSet.reps } : {}),
        weight: (parseFloat(String(currentSet.weight)) || 0) * 0.75, // Suggest 75% of main set weight
        assistance: false,
        rpe: currentSet.rpe,
        isDropSet: true,
        dropSetOrder: nextDropSetOrder + 1,
        parentSetIndex: setIndex,
      };

      // Insert after the last drop set or after the main set
      const insertIndex = setIndex + existingDropSets.length + 1;
      updated[exerciseIndex].sets.splice(insertIndex, 0, newDropSet);

      return updated;
    });
  };

  const handleRemoveSet = (exerciseIndex: number, setIndex: number) => {
    setExerciseLogs((prev) => {
      const updated = [...prev];
      updated[exerciseIndex].sets.splice(setIndex, 1);
      return updated;
    });
  };

  const handleToggleSkip = (exerciseIndex: number) => {
    setExerciseLogs((prev) =>
      prev.map((log, i) =>
        i !== exerciseIndex ? log : { ...log, skipped: !log.skipped }
      )
    );
  };

  const handleToggleUnilateral = (exerciseIndex: number) => {
    setExerciseLogs((prev) =>
      prev.map((log, i) => {
        if (i !== exerciseIndex) return log;
        const newOverride = !log.unilateralOverride;
        const updatedSets = log.sets.map((set) => {
          if (newOverride) {
            // Switching to unilateral: initialize left/right from current reps
            return { ...set, repsLeft: set.repsLeft ?? set.reps, repsRight: set.repsRight ?? set.reps };
          } else {
            // Switching to bilateral: compute reps as average of left+right
            const avgReps = (set.repsLeft != null && set.repsRight != null)
              ? Math.round((set.repsLeft + set.repsRight) / 2)
              : set.reps;
            return { ...set, reps: avgReps, repsLeft: undefined, repsRight: undefined };
          }
        });
        return { ...log, unilateralOverride: newOverride, sets: updatedSets };
      })
    );
  };

  const handleOpenSubstitute = async (exerciseIndex: number) => {
    try {
      setSubstituteLoading(true);
      const [muscleGroups, equipment, movementPatterns, history] = await Promise.all([
        ExerciseService.getMuscleGroups(),
        ExerciseService.getEquipmentTypes(),
        ExerciseService.getMovementPatterns(),
        ExerciseService.getExercisesWithHistory(),
      ]);

      setSubstituteMuscleGroups(muscleGroups);
      setSubstituteEquipmentOptions(equipment);
      setSubstituteMovementPatterns(movementPatterns);
      setSubstituteHistory(history);
      setSubstituteExerciseIndex(exerciseIndex);
      // Pre-fill filter with current exercise's muscle group
      const currentExercise = exerciseLogs[exerciseIndex]?.substitutedWith || exerciseLogs[exerciseIndex]?.programExercise.exercise;
      setSubstituteFilterMuscle(currentExercise?.muscleGroup || '');
      setSubstituteFilterEquipment('');
      setSubstituteFilterMovementPattern('');
      setSubstituteSearch('');
      setSelectedSubstitute(null);
      setShowCreateExercise(false);
      setSubstituteDialogOpen(true);
    } catch (err) {
      setError('Грешка при зареждане на упражненията');
      console.error(err);
    } finally {
      setSubstituteLoading(false);
    }
  };

  const resetSubstituteFilters = useCallback(() => {
    setSubstituteSearch('');
    setSubstituteFilterMuscle('');
    setSubstituteFilterEquipment('');
    setSubstituteFilterMovementPattern('');
    setSelectedSubstitute(null);
  }, []);

  useEffect(() => {
    if (!substituteDialogOpen) {
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        setSubstituteLoading(true);
        const result = await ExerciseService.searchExercises({
          q: substituteSearch || undefined,
          muscleGroup: substituteFilterMuscle || undefined,
          equipment: substituteFilterEquipment || undefined,
          movementPattern: substituteFilterMovementPattern || undefined,
          limit: 100,
        });

        setAvailableExercises(result.exercises);
      } catch (err) {
        setError('Грешка при филтриране на упражненията');
        console.error(err);
      } finally {
        setSubstituteLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [
    substituteDialogOpen,
    substituteFilterEquipment,
    substituteFilterMovementPattern,
    substituteFilterMuscle,
    substituteSearch,
  ]);

  const substituteHistoryMap = useMemo(() => {
    return new Map(substituteHistory.map((entry) => [entry.id, entry]));
  }, [substituteHistory]);

  const orderedSubstituteExercises = useMemo(() => {
    return [...availableExercises].sort((left, right) => {
      const leftHistory = substituteHistoryMap.get(left.id);
      const rightHistory = substituteHistoryMap.get(right.id);
      const leftHasHistory = !!leftHistory;
      const rightHasHistory = !!rightHistory;

      if (leftHasHistory !== rightHasHistory) {
        return leftHasHistory ? -1 : 1;
      }

      if (leftHasHistory && rightHasHistory) {
        const leftTime = leftHistory?.lastPerformedAt ? new Date(leftHistory.lastPerformedAt).getTime() : 0;
        const rightTime = rightHistory?.lastPerformedAt ? new Date(rightHistory.lastPerformedAt).getTime() : 0;

        if (leftTime !== rightTime) {
          return rightTime - leftTime;
        }
      }

      return left.name.localeCompare(right.name);
    });
  }, [availableExercises, substituteHistoryMap]);

  const applyLastWorkoutWeightsToSets = (
    sets: SetInput[],
    lastWorkoutData: Awaited<ReturnType<typeof ExerciseService.getLastWorkoutData>>
  ): SetInput[] => {
    return sets.map((set, index) => {
      const lastSet = lastWorkoutData?.sets?.[index];
      const lastWeight = typeof lastSet?.weight === 'number' ? lastSet.weight : 0;
      return { ...set, weight: lastWeight };
    });
  };

  const handleConfirmSubstitute = async () => {
    if (substituteExerciseIndex === null || !selectedSubstitute) return;

    try {
      setLoading(true);
      const lastWorkoutData = await ExerciseService.getLastWorkoutData(selectedSubstitute.id);
      setExerciseLogs((prev) => {
        const updated = [...prev];
        const currentLog = updated[substituteExerciseIndex];
        if (!currentLog) return prev;

        updated[substituteExerciseIndex] = {
          ...currentLog,
          substitutedWith: selectedSubstitute,
          sets: applyLastWorkoutWeightsToSets(currentLog.sets, lastWorkoutData),
          lastWorkoutSummary: lastWorkoutData?.summary || undefined,
        };
        return updated;
      });
      setSubstituteDialogOpen(false);
      setSubstituteExerciseIndex(null);
      setSelectedSubstitute(null);
    } catch (err) {
      setError('Грешка при замяна на упражнението');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddExercise = async () => {
    try {
      setLoading(true);
      const exercises = await ExerciseService.getAll();
      setAvailableExercises(exercises);
      setSelectedSubstitute(null);
      setAddExerciseDialogOpen(true);
    } catch (err) {
      setError('Грешка при зареждане на упражненията');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAddExercise = async () => {
    if (!selectedSubstitute || selectedDay === null) return;

    try {
      setLoading(true);
      const lastWorkoutData = await ExerciseService.getLastWorkoutData(selectedSubstitute.id);
      const sets: SetInput[] = [];

      const isUni = !!selectedSubstitute.unilateral;
      if (lastWorkoutData?.sets?.length) {
        lastWorkoutData.sets.forEach((lastSet, index) => {
          sets.push({
            setNumber: (index + 1).toString(),
            reps: lastSet.reps,
            ...(isUni ? { repsLeft: lastSet.repsLeft ?? lastSet.reps, repsRight: lastSet.repsRight ?? lastSet.reps } : {}),
            weight: typeof lastSet.weight === 'number' ? lastSet.weight : 0,
            assistance: !!lastSet.assistance,
            rpe: typeof lastSet.rpe === 'number' ? lastSet.rpe : 7,
            isDropSet: false,
          });
        });
      } else {
        for (let i = 1; i <= 3; i++) {
          sets.push({
            setNumber: i.toString(),
            reps: 10,
            ...(isUni ? { repsLeft: 10, repsRight: 10 } : {}),
            weight: 0,
            assistance: false,
            rpe: 7,
            isDropSet: false,
          });
        }
      }

      const nextExerciseIndex = exerciseLogs.length;
      // steps may have fewer entries than exerciseLogs when superset groups exist,
      // so use steps.length (not exerciseLogs.length) as the new step index.
      const nextStepIndex = steps.length;
      const adhocProgramExercise: ProgramExercise = {
        id: 0,
        exercise: selectedSubstitute,
        dayOfWeek: selectedDay,
        orderInSession: nextExerciseIndex + 1,
        createdAt: '',
      };

      setExerciseLogs((prev) => ([
        ...prev,
        {
          programExercise: adhocProgramExercise,
          sets,
          skipped: false,
          lastWorkoutSummary: lastWorkoutData?.summary || undefined,
          supersetGroup: null,
        },
      ]));

      setCurrentSetIndices((prev) => ({ ...prev, [nextExerciseIndex]: 0 }));
      setCurrentStepIndex(nextStepIndex);
      setAddExerciseDialogOpen(false);
      setSelectedSubstitute(null);
    } catch (err) {
      setError('Грешка при добавяне на упражнение');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Quick-create exercise from substitute dialog
  const handleQuickCreateExercise = async () => {
    if (!newExerciseName.trim() || !newExerciseMuscleGroup || !newExerciseCategory) return;
    try {
      setCreatingExercise(true);
      const input: CreateExerciseInput = {
        name: newExerciseName.trim(),
        muscleGroup: newExerciseMuscleGroup,
        category: newExerciseCategory,
        stimulusToFatigueRatio: '5.00',
        equipment: '',
        difficulty: 'intermediate',
      };
      const created = await ExerciseService.create(input);
      // Add to available exercises list and select it
      setAvailableExercises(prev => [...prev, created]);
      setSelectedSubstitute(created);
      setShowCreateExercise(false);
      setNewExerciseName('');
      setNewExerciseMuscleGroup('');
      setNewExerciseCategory('');
    } catch (err) {
      setError('Грешка при създаване на упражнение');
      console.error(err);
    } finally {
      setCreatingExercise(false);
    }
  };

  const handleOpenVariationSwitcher = async (exerciseIndex: number) => {
    setVariationExerciseIndex(exerciseIndex);
    const log = exerciseLogs[exerciseIndex];
    const exercise = log.substitutedWith || log.programExercise.exercise;
    const baseId = exercise.parentExerciseId || exercise.id;
    setLoadingVariationOptions(true);
    setVariationDialogOpen(true);
    try {
      const data = await ExerciseService.getVariations(baseId);
      setVariationOptions([data.baseExercise, ...data.variations]);
      setAlternativeOptions(data.alternatives || []);
    } catch (err) {
      console.error('Failed to load variations:', err);
      setVariationOptions([]);
      setAlternativeOptions([]);
    } finally {
      setLoadingVariationOptions(false);
    }
  };

  const handleSelectVariation = async (variation: Exercise) => {
    setVariationDialogOpen(false);
    setExerciseLogs((prev) => prev.map((log, i) =>
      i !== variationExerciseIndex ? log : { ...log, substitutedWith: variation }
    ));
    try {
      const lastWorkout = await ExerciseService.getLastWorkoutData(variation.id);
      setExerciseLogs((prev) => prev.map((log, i) => {
        if (i !== variationExerciseIndex) return log;
        return {
          ...log,
          sets: applyLastWorkoutWeightsToSets(log.sets, lastWorkout),
          lastWorkoutSummary: lastWorkout?.summary || undefined,
        };
      }));
    } catch (err) {
      console.error('Failed to fetch last workout for variation:', err);
    }
  };

  // Navigation functions for step-by-step mode
  const goToNextExercise = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      // Mark current step as completed
      setCompletedSteps((prev) => new Set([...Array.from(prev), currentStepIndex]));
      setCurrentStepIndex((prev) => prev + 1);
      setShowDetailsMap({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStepIndex, steps.length]);

  const goToPrevExercise = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
      setShowDetailsMap({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStepIndex]);

  // Single-set navigation helpers
  const getCurrentSetIndex = (exerciseIndex: number) => currentSetIndices[exerciseIndex] ?? 0;
  const getTotalSets = (exerciseIndex: number) => exerciseLogs[exerciseIndex]?.sets.length ?? 0;

  const goToNextSet = useCallback((exerciseIndex: number) => {
    const total = getTotalSets(exerciseIndex);
    setCurrentSetIndices(prev => ({
      ...prev,
      [exerciseIndex]: Math.min((prev[exerciseIndex] ?? 0) + 1, total - 1),
    }));
  }, [exerciseLogs]);

  const goToPrevSet = useCallback((exerciseIndex: number) => {
    setCurrentSetIndices(prev => ({
      ...prev,
      [exerciseIndex]: Math.max((prev[exerciseIndex] ?? 0) - 1, 0),
    }));
  }, []);

  // Interactive mode handlers
  const handleInteractiveStart = useCallback(() => {
    setInteractiveSetStartedAt(Date.now());
    setInteractiveSetElapsed(0);
    setInteractiveSetPhase('executing');
  }, []);

  const handleInteractiveDone = useCallback(() => {
    const elapsed = interactiveSetStartedAt
      ? Math.floor((Date.now() - interactiveSetStartedAt) / 1000)
      : 0;

    // Save duration to the current set
    const step = steps[currentStepIndex];
    if (step) {
      const exIdx = step.exerciseIndices[interactivePosition.exerciseOffset];
      if (exIdx !== undefined) {
        // Track last completed set for rest-phase editing
        interactiveLastCompletedRef.current = { exerciseIndex: exIdx, setIndex: interactivePosition.setIndex };
        setInteractiveRestEditOpen(false);
        setExerciseLogs((prev) => {
          const updated = [...prev];
          const log = { ...updated[exIdx], sets: [...updated[exIdx].sets] };
          const setIdx = interactivePosition.setIndex;
          if (log.sets[setIdx]) {
            log.sets[setIdx] = { ...log.sets[setIdx], durationSeconds: elapsed, restSeconds: interactiveRestSeconds, completed: true };
          }
          updated[exIdx] = log;
          return updated;
        });
      }
    }

    // Determine if more sets/exercises remain
    if (!step) return;
    const { exerciseOffset, setIndex } = interactivePosition;
    const nextOffset = exerciseOffset + 1;
    const hasMoreInRound = nextOffset < step.exerciseIndices.length;
    const nextSetIndex = setIndex + 1;
    const allDoneAfterWrap = step.exerciseIndices.every((exIdx) =>
      nextSetIndex >= (exerciseLogs[exIdx]?.sets.length ?? 0)
    );

    if (hasMoreInRound || !allDoneAfterWrap) {
      // More sets remain — start rest
      setInteractiveRestRemaining(interactiveRestSeconds);
      setInteractiveSetPhase('resting');
    } else {
      // All done for this step
      setInteractiveSetPhase('exercise-summary');
    }

    setInteractiveSetStartedAt(null);
    setInteractiveSetElapsed(0);
  }, [interactiveSetStartedAt, steps, currentStepIndex, interactivePosition, interactiveRestSeconds, exerciseLogs]);

  const handleInteractiveSkipRest = useCallback(() => {
    setInteractiveRestRemaining(0);
    // The auto-advance effect will fire
  }, []);

  // Quick save: log a set with manual duration without going through start/execute/done
  const handleQuickSave = useCallback(() => {
    const manualDuration = quickSaveMinutes * 60 + quickSaveSeconds;

    const step = steps[currentStepIndex];
    if (step) {
      const exIdx = step.exerciseIndices[interactivePosition.exerciseOffset];
      if (exIdx !== undefined) {
        interactiveLastCompletedRef.current = { exerciseIndex: exIdx, setIndex: interactivePosition.setIndex };
        setInteractiveRestEditOpen(false);
        setExerciseLogs((prev) => {
          const updated = [...prev];
          const log = { ...updated[exIdx], sets: [...updated[exIdx].sets] };
          const setIdx = interactivePosition.setIndex;
          if (log.sets[setIdx]) {
            log.sets[setIdx] = { ...log.sets[setIdx], durationSeconds: manualDuration, restSeconds: interactiveRestSeconds, completed: true };
          }
          updated[exIdx] = log;
          return updated;
        });
      }
    }

    if (!step) return;
    const { exerciseOffset, setIndex } = interactivePosition;
    const nextOffset = exerciseOffset + 1;
    const hasMoreInRound = nextOffset < step.exerciseIndices.length;
    const nextSetIndex = setIndex + 1;
    const allDoneAfterWrap = step.exerciseIndices.every((exIdx) =>
      nextSetIndex >= (exerciseLogs[exIdx]?.sets.length ?? 0)
    );

    if (hasMoreInRound || !allDoneAfterWrap) {
      setInteractiveRestRemaining(interactiveRestSeconds);
      setInteractiveSetPhase('resting');
    } else {
      setInteractiveSetPhase('exercise-summary');
    }

    setQuickSaveOpen(false);
    setQuickSaveMinutes(0);
    setQuickSaveSeconds(45);
    setInteractiveSetStartedAt(null);
    setInteractiveSetElapsed(0);
  }, [quickSaveMinutes, quickSaveSeconds, steps, currentStepIndex, interactivePosition, interactiveRestSeconds, exerciseLogs]);

  const handleInteractiveConfirmExercise = useCallback(() => {
    // Mark current step as completed and go to next
    if (currentStepIndex < steps.length - 1) {
      setCompletedSteps((prev) => new Set([...Array.from(prev), currentStepIndex]));
      setCurrentStepIndex((prev) => prev + 1);
      setShowDetailsMap({});
    }
    setInteractivePosition({ exerciseOffset: 0, setIndex: 0 });
    setInteractiveSetPhase('ready');
    setInteractiveSetStartedAt(null);
    setInteractiveSetElapsed(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStepIndex, steps.length]);

  const isLastExercise = currentStepIndex === steps.length - 1;
  const isFirstExercise = currentStepIndex === 0;
  const currentStep = steps[currentStepIndex];
  const currentExerciseIndices = currentStep?.exerciseIndices || [0];
  const progress = steps.length > 0 ? ((currentStepIndex + 1) / steps.length) * 100 : 0;
  const isIntroActive = workoutType === 'strength' && currentSession !== null && !introCompleted && introPhase !== 'complete';

  // Use dynamic warmup items when available, otherwise fall back to static slides
  type EffectiveSlide = WarmupItem & { imageUrl?: string | null };
  const effectiveWarmupSlides: EffectiveSlide[] = warmupPlanItems !== null
    ? (warmupPlanItems as EffectiveSlide[])
    : WARMUP_GUIDE_SLIDES.filter((slide) => slide.imageUrl !== null).map((slide, idx): EffectiveSlide => ({
        name: slide.title,
        description: slide.description,
        orderIndex: idx,
        imageUrl: slide.imageUrl,
      }));
  const isDynamicWarmup = warmupPlanItems !== null;
  const allWarmupImagesFailed = !isDynamicWarmup && WARMUP_GUIDE_SLIDES.filter((s) => s.imageUrl !== null).length > 0
    && WARMUP_GUIDE_SLIDES.filter((s) => s.imageUrl !== null).every((_, index) => warmupImageErrors[index]);
  const currentWarmupSlide = effectiveWarmupSlides[warmupGuideIndex] || null;
  const stretchingContent = useMemo(() => getWorkoutStretchingContent(), []);

  // Quick increment/decrement functions for mobile
  const adjustWeight = (exerciseIndex: number, setIndex: number, delta: number) => {
    setExerciseLogs((prev) =>
      prev.map((log, i) =>
        i !== exerciseIndex ? log : {
          ...log,
          sets: log.sets.map((set, j) =>
            j !== setIndex ? set : { ...set, weight: Math.max(0, (parseFloat(String(set.weight)) || 0) + delta) }
          ),
        }
      )
    );
  };

  const adjustReps = (exerciseIndex: number, setIndex: number, delta: number) => {
    setExerciseLogs((prev) =>
      prev.map((log, i) =>
        i !== exerciseIndex ? log : {
          ...log,
          sets: log.sets.map((set, j) =>
            j !== setIndex ? set : { ...set, reps: Math.max(0, set.reps + delta) }
          ),
        }
      )
    );
  };

  // Image viewer functions
  const openImageViewer = (images: string[], startIndex: number, title: string) => {
    setImageViewerImages(images);
    setImageViewerIndex(startIndex);
    setImageViewerTitle(title);
    setImageViewerOpen(true);
  };

  const handleImageViewerPrev = () => {
    setImageViewerIndex((prev) => (prev === 0 ? imageViewerImages.length - 1 : prev - 1));
  };

  const handleImageViewerNext = () => {
    setImageViewerIndex((prev) => (prev === imageViewerImages.length - 1 ? 0 : prev + 1));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleImageViewerNext();
      } else {
        handleImageViewerPrev();
      }
    }
    setTouchStart(null);
  };

  const hasDetailContent = (exercise: Exercise) => {
    return !!(
      exercise.instructions ||
      exercise.youtubeVideoUrl ||
      (exercise.anatomyImages && exercise.anatomyImages.length > 0) ||
      (exercise.instructionImages && exercise.instructionImages.length > 0)
    );
  };

  // Drag & drop sensors for exercise drawer
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } });
  const drawerSensors = useSensors(pointerSensor, touchSensor);

  const handleExerciseDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = exerciseLogs.findIndex((_, i) => i === Number(active.id));
    const newIndex = exerciseLogs.findIndex((_, i) => i === Number(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    setExerciseLogs((prev) => arrayMove(prev, oldIndex, newIndex));
  }, [exerciseLogs]);

  // Remove exercise from workout
  const handleRemoveExercise = (exerciseIndex: number) => {
    if (exerciseLogs.length <= 1) {
      setError('Не можеш да премахнеш последното упражнение');
      return;
    }

    setExerciseLogs((prev) => {
      const updated = [...prev];
      updated.splice(exerciseIndex, 1);
      return updated;
    });

    // Adjust current step index if needed (steps will recompute from new exerciseLogs)
    if (currentStepIndex >= steps.length - 1) {
      setCurrentStepIndex(Math.max(0, steps.length - 2));
    }
  };

  const handleCancelWorkout = async () => {
    if (!currentSession) return;
    try {
      await WorkoutSessionService.update(currentSession.id, { status: 'skipped' });
    } catch (err) {
      console.error('Failed to mark session as skipped:', err);
    }
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(TIMER_STORAGE_KEY);
    setSelectedDay(null);
    setCurrentSession(null);
    setExerciseLogs([]);
    setWorkoutStartedAt(null);
    setTimerState(null);
    setIntroCompleted(true);
    setIntroPhase('complete');
  };

  const handleCloseCompletionOutro = useCallback(() => {
    saveInProgressRef.current = false;
    setCompletionSummary(null);
    setCompletionOutroStep('summary');
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(TIMER_STORAGE_KEY);
    setSelectedDay(null);
    setExerciseLogs([]);
    setCurrentSession(null);
    setWorkoutStartedAt(null);
    setTimerState(null);
    setIntroCompleted(true);
    setIntroPhase('complete');
  }, []);

  const handleSaveWorkout = async () => {
    if (!currentSession) return;

    // Synchronous ref guard — prevents re-entry even before React re-renders
    if (saveInProgressRef.current) return;
    saveInProgressRef.current = true;

    try {
      setLoading(true);

      // Acquire server-side completion lock (10-minute TTL)
      const lockAcquired = await WorkoutSessionService.acquireCompletionLock(currentSession.id);
      if (!lockAcquired) {
        setError('Тренировката вече се записва или е завършена. Моля, изчакайте.');
        return;
      }

      const normalizedSessionNotes = currentSession.notes?.trim() || null;

      // Idempotency: check if sets already exist (from a previous failed attempt)
      let setsAlreadyExist = false;
      try {
        const existingLog = await WorkoutSessionService.getLogData(currentSession.id);
        if (existingLog.exercises && existingLog.exercises.length > 0) {
          setsAlreadyExist = true;
        }
      } catch {
        // If getLogData fails, fall through to normal set creation
      }

      // Save all sets for non-skipped exercises (skip if sets already exist from a prior attempt)
      if (!setsAlreadyExist) {
        for (const exerciseLog of exerciseLogs) {
          if (exerciseLog.skipped) continue;

          const exerciseId = exerciseLog.substitutedWith
            ? exerciseLog.substitutedWith.id
            : exerciseLog.programExercise.exercise.id;

          // Map to track created set IDs for drop sets
          const createdSetIds: { [index: number]: number } = {};

          for (let i = 0; i < exerciseLog.sets.length; i++) {
            const set = exerciseLog.sets[i];

            // For drop sets, find the parent set ID
            let parentSetId: number | undefined = undefined;
            if (set.isDropSet && set.parentSetIndex !== undefined) {
              parentSetId = createdSetIds[set.parentSetIndex];
            }

            const currentExercise = exerciseLog.substitutedWith || exerciseLog.programExercise.exercise;
            const isUnilateral = !!currentExercise.unilateral || !!exerciseLog.unilateralOverride;

            const setInput: CreateSetInput = {
              reps: isUnilateral
                ? Math.round(((set.repsLeft ?? 0) + (set.repsRight ?? 0)) / 2)
                : set.reps,
              repsLeft: isUnilateral ? (set.repsLeft ?? 0) : undefined,
              repsRight: isUnilateral ? (set.repsRight ?? 0) : undefined,
              weight: (parseFloat(String(set.weight)) || 0) > 0 ? String(parseFloat(String(set.weight))) : undefined,
              setOrder: i + 1,
              assistance: set.assistance,
              rpe: set.rpe,
              notes: set.notes || undefined,
              durationSeconds: set.durationSeconds || undefined,
              restSeconds: set.restSeconds || undefined,
              dropSetOrder: set.dropSetOrder,
              parentSetId,
              isSubstitution: !!exerciseLog.substitutedWith,
              exercise: `/api/exercises/${exerciseId}`,
              workoutSession: `/api/workout_sessions/${currentSession.id}`,
            };

            const createdSet = await SetService.create(setInput);
            createdSetIds[i] = createdSet.id;
          }
        }
      }

      // Compute final paused seconds (if currently paused, add the current pause duration)
      let finalPausedSeconds = timerState?.totalPausedSeconds ?? 0;
      if (timerState?.isPaused && timerState.pausedAt) {
        finalPausedSeconds += Math.floor((Date.now() - new Date(timerState.pausedAt).getTime()) / 1000);
      }

      const completedAtIso = new Date().toISOString();

      // Update session status to completed with time tracking data
      // Use workoutStartedAt (when the user actually pressed start), not session createdAt
      // (which for planned workouts could be days/weeks earlier)
      const durationMinutes = workoutStartedAt
        ? Math.round((Date.now() - new Date(workoutStartedAt).getTime()) / 60000)
        : undefined;

      // Update session status to completed with duration
      let completedSession;
      try {
        completedSession = await WorkoutSessionService.update(currentSession.id, {
          status: 'completed',
          notes: normalizedSessionNotes,
          durationMinutes: durationMinutes && durationMinutes > 0 ? durationMinutes : undefined,
          completedAt: completedAtIso,
          totalPausedSeconds: finalPausedSeconds,
        });
      } catch (patchErr: any) {
        // If session is already completed (409), treat as success
        if (patchErr?.response?.status === 409) {
          completedSession = await WorkoutSessionService.getById(currentSession.id);
        } else {
          throw patchErr;
        }
      }
      setCurrentSession(completedSession);

      const effectiveCompletedAtIso = completedSession.completedAt ?? completedAtIso;

      window.dispatchEvent(new CustomEvent(WORKOUT_SESSION_UPDATED_EVENT));
      window.dispatchEvent(new CustomEvent('trainer-schedule-updated'));

      // Advance rotation if this day has a rotational config
      if (selectedProgram && selectedDay !== null) {
        try {
          await RotationalTrainingService.advance(selectedProgram.id, selectedDay);
        } catch {
          // Non-critical - rotation may not exist for this day
        }
      }

      // Calculate summary data
      if (workoutStartedAt) {
        const totalSeconds = Math.floor((new Date(effectiveCompletedAtIso).getTime() - new Date(workoutStartedAt).getTime()) / 1000);
        const activeSeconds = Math.max(0, totalSeconds - finalPausedSeconds);
        const exerciseCount = exerciseLogs.filter(l => !l.skipped).length;
        const setCount = exerciseLogs.filter(l => !l.skipped).reduce((sum, l) => sum + l.sets.length, 0);

        setCompletionSummary({
          durationMinutes: Math.ceil(totalSeconds / 60),
          activeMinutes: Math.ceil(activeSeconds / 60),
          pauseMinutes: Math.ceil(finalPausedSeconds / 60),
          exerciseCount,
          setCount,
        });
        setCompletionOutroStep('summary');
      } else {
        // No timer data, just show simple alert
        setCompletionSummary(null);
      }

      if (!workoutStartedAt) {
        alert('Тренировката е запазена успешно!');
        handleCloseCompletionOutro();
      }
      // If workoutStartedAt exists, we show summary dialog and clean up from there
    } catch (err: any) {
      const apiMessage = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.response?.data?.violations?.[0]?.message;
      setError(apiMessage || 'Грешка при запазване на тренировката');
      console.error(err);
      // Release the ref guard on error so the user can retry
      saveInProgressRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // If cardio selected (free or after day selection), show CardioSessionLogger
  if (workoutType === 'cardio') {
    return (
      <Box sx={{ pb: 12, minHeight: '100vh', bgcolor: 'background.default' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            py: 1.5,
          }}
        >
          <Button
            onClick={() => {
              setWorkoutType(null);
              if (!selectedProgram) {
                // Free cardio - go back to program selection
              } else {
                // Cardio from day selection - go back to type selection
              }
            }}
            size="small"
            sx={{ minWidth: 'auto', px: 1 }}
          >
            ← Назад
          </Button>
        </Box>
        {/* Lazy load CardioSessionLogger */}
        <Box sx={{ p: 2 }}>
          <React.Suspense fallback={<Box>Зареждане...</Box>}>
            {/* CardioSessionLogger component */}
            <CardioSessionLogger onClose={() => setWorkoutType(null)} />
          </React.Suspense>
        </Box>
      </Box>
    );
  }

  if (!selectedProgram) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Избери програма
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Избери програма за да започнеш тренировка
        </Typography>
        <Stack spacing={2}>
          {programs.map((program) => (
            <Card
              key={program.id}
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 3 },
                '&:active': { transform: 'scale(0.98)' },
                transition: 'all 0.15s',
              }}
              onClick={() => handleProgramSelect(program)}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" fontWeight="bold">{program.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {program.description || 'Няма описание'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`${program.workoutsPerWeek} дни/седмица`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`${program.durationWeeks} седмици`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => handleWorkoutTypeSelect('cardio')}
            color="secondary"
            sx={{
              py: 2.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
            }}
          >
            🏃 Свободна кардио сесия
          </Button>
        </Box>
      </Box>
    );
  }

  if (selectedDay === null) {
    const days = Object.keys(programDays).map(Number).sort((a, b) => a - b);
    const plannedDaysList = pendingStatus?.plannedDays || [];
    const inProgressDaysList = pendingStatus?.inProgressDays || [];
    const completedDaysList = pendingStatus?.completedDays || [];

    return (
      <Box sx={{ p: 2 }}>
        <Button onClick={() => setSelectedProgram(null)} sx={{ mb: 2 }}>
          ← Назад към програми
        </Button>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Избери ден
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedProgram.name}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Gym selector */}
        {gyms.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="workout-day-gym-select-label">Зала</InputLabel>
              <Select
                labelId="workout-day-gym-select-label"
                id="workout-day-gym-select"
                value={selectedGymId || ''}
                label="Зала"
                onChange={(e) => setSelectedGymId(e.target.value ? Number(e.target.value) : null)}
              >
                <MenuItem value="">Без зала</MenuItem>
                {gyms.map((gym) => (
                  <MenuItem key={gym.id} value={gym.id}>
                    {gym.name}{gym.id === defaultGymId ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedGymId && selectedGymId !== defaultGymId && (
              <Button
                variant="text"
                size="small"
                sx={{ mt: 0.5, px: 0 }}
                onClick={handleSetDefaultGym}
                disabled={savingDefaultGym}
              >
                {savingDefaultGym ? 'Запазване...' : 'Задай като default'}
              </Button>
            )}
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="workout-day-training-label-select-label">Label на тренировка</InputLabel>
            <Select
              labelId="workout-day-training-label-select-label"
              id="workout-day-training-label-select"
              value={selectedTrainingLabel || ''}
              label="Label на тренировка"
              onChange={(e) => setSelectedTrainingLabel((e.target.value || null) as TrainingLabel | null)}
            >
              <MenuItem value="">Без label</MenuItem>
              {TRAINING_LABEL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Stack spacing={1.5}>
          {days.map((day) => {
            const isPlanned = plannedDaysList.includes(day);
            const isInProgress = inProgressDaysList.includes(day);
            const isCompleted = completedDaysList.includes(day);

            return (
              <Card
                key={day}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                  '&:active': { transform: 'scale(0.98)' },
                  transition: 'all 0.15s',
                  opacity: isCompleted ? 0.5 : 1,
                  borderLeft: (isPlanned || isInProgress) ? 4 : 0,
                  borderLeftColor: isInProgress ? 'warning.main' : isPlanned ? 'info.main' : 'transparent',
                }}
                onClick={() => handleDaySelect(day)}
              >
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {dayNames[day] || `Ден ${day}`}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {programDays[day]?.length || 0} упражнения
                        </Typography>
                        {isInProgress && (
                          <Chip label="В прогрес" size="small" color="warning" icon={<FitnessIcon />} sx={{ height: 22 }} />
                        )}
                        {isPlanned && (
                          <Chip label="Планиран" size="small" color="info" icon={<EventNoteIcon />} sx={{ height: 22 }} />
                        )}
                        {isCompleted && (
                          <Chip label="Завършен" size="small" color="success" icon={<CheckCircleIcon />} sx={{ height: 22 }} />
                        )}
                      </Stack>
                    </Box>
                    <NextIcon color="action" />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        {/* Unplanned Workout Confirmation Dialog */}
        <Dialog
          open={confirmUnplannedOpen}
          onClose={() => setConfirmUnplannedOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Без план</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Нямаш план за тази тренировка. Искаш ли да продължиш без план?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setConfirmUnplannedOpen(false);
                if (pendingDay !== null) {
                  startWorkoutWithoutPlan(pendingDay);
                }
              }}
            >
              Продължи без план
            </Button>
            {onNavigateToPlanner && (
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setConfirmUnplannedOpen(false);
                  onNavigateToPlanner();
                }}
              >
                Планирай първо
              </Button>
            )}
            <Button
              fullWidth
              onClick={() => setConfirmUnplannedOpen(false)}
            >
              Откажи
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={checkInDialogOpen}
          onClose={closeCheckInDialog}
          maxWidth="sm"
          fullWidth
          data-testid="check-in-dialog"
        >
          <DialogTitle>
            {isCheckInReviewStep ? 'Преглед на AI промени' : 'Check-in преди старт'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack spacing={1}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  data-testid="check-in-progress-text"
                >
                  {`Въпрос ${Math.min(checkInStepIndex + 1, checkInSteps.length)} от ${checkInSteps.length}`}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={checkInProgressValue}
                  sx={{ height: 10, borderRadius: 999 }}
                  data-testid="check-in-progress-bar"
                />
              </Stack>

              {!isCheckInReviewStep && currentCheckInStep && (
                <Card variant="outlined" data-testid={`check-in-step-${currentCheckInStep.key}`}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={3} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 72,
                          height: 72,
                          borderRadius: '24px',
                          bgcolor: 'action.hover',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {currentCheckInStep.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                          {currentCheckInStep.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {currentCheckInStep.description}
                        </Typography>
                      </Box>

                      {currentCheckInStep.key === 'availableTimeMinutes' && (
                        <TextField
                          type="number"
                          label="Свободни минути"
                          value={checkInData.availableTimeMinutes || ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setCheckInData((prev) => ({ ...prev, availableTimeMinutes: raw === '' ? 0 : parseInt(raw, 10) || 0 }));
                          }}
                          inputProps={{ min: 5, max: 240 }}
                          required
                          fullWidth
                        />
                      )}

                      {currentCheckInStep.key === 'fatigueLevel' && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {Array.from({ length: 10 }, (_, index) => {
                            const value = index + 1;
                            return (
                              <Button
                                key={`fatigue-${value}`}
                                variant={checkInData.fatigueLevel === value ? 'contained' : 'outlined'}
                                onClick={() => setCheckInData((prev) => ({ ...prev, fatigueLevel: value }))}
                              >
                                {value}
                              </Button>
                            );
                          })}
                        </Stack>
                      )}

                      {currentCheckInStep.key === 'hasPain' && (
                        <Stack direction="row" spacing={1.5}>
                          <Button
                            variant={checkInData.hasPain ? 'contained' : 'outlined'}
                            color={checkInData.hasPain ? 'warning' : 'inherit'}
                            onClick={() => setCheckInData((prev) => ({ ...prev, hasPain: true }))}
                          >
                            Да
                          </Button>
                          <Button
                            variant={!checkInData.hasPain ? 'contained' : 'outlined'}
                            onClick={() => setCheckInData((prev) => ({ ...prev, hasPain: false, painZone: '' }))}
                          >
                            Не
                          </Button>
                        </Stack>
                      )}

                      {currentCheckInStep.key === 'painZone' && (
                        <TextField
                          label="Зона на болка"
                          value={checkInData.painZone || ''}
                          onChange={(e) => setCheckInData((prev) => ({ ...prev, painZone: e.target.value }))}
                          inputProps={{ maxLength: 120 }}
                          required
                          fullWidth
                        />
                      )}

                      {currentCheckInStep.key === 'sleepQuality' && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {Array.from({ length: 10 }, (_, index) => {
                            const value = index + 1;
                            return (
                              <Button
                                key={`sleep-${value}`}
                                variant={checkInData.sleepQuality === value ? 'contained' : 'outlined'}
                                onClick={() => setCheckInData((prev) => ({ ...prev, sleepQuality: value }))}
                              >
                                {value}
                              </Button>
                            );
                          })}
                        </Stack>
                      )}

                      {currentCheckInStep.key === 'goalFocus' && (
                        <Stack spacing={1.5} sx={{ width: '100%' }}>
                          {[
                            { value: 'performance', label: 'Производителност' },
                            { value: 'technique', label: 'Техника' },
                            { value: 'recovery', label: 'Възстановяване' },
                            { value: 'maintenance', label: 'Поддръжка' },
                          ].map((option) => (
                            <Button
                              key={option.value}
                              variant={checkInData.goalFocus === option.value ? 'contained' : 'outlined'}
                              onClick={() => setCheckInData((prev) => ({ ...prev, goalFocus: option.value as WorkoutCheckInPayload['goalFocus'] }))}
                              sx={{ justifyContent: 'flex-start' }}
                            >
                              {option.label}
                            </Button>
                          ))}
                        </Stack>
                      )}

                      {currentCheckInStep.key === 'missingEquipmentIds' && adaptationContext?.equipmentOptions && (
                        <Stack spacing={1} sx={{ width: '100%' }}>
                          {adaptationContext.equipmentOptions.map((equipment) => {
                            const selected = (checkInData.missingEquipmentIds || []).includes(equipment.id);

                            return (
                              <Button
                                key={equipment.id}
                                variant={selected ? 'contained' : 'outlined'}
                                onClick={() => setCheckInData((prev) => {
                                  const currentIds = prev.missingEquipmentIds || [];
                                  const nextIds = selected
                                    ? currentIds.filter((id) => id !== equipment.id)
                                    : [...currentIds, equipment.id];

                                  return {
                                    ...prev,
                                    missingEquipmentIds: nextIds,
                                  };
                                })}
                                sx={{ justifyContent: 'flex-start' }}
                              >
                                {equipment.name}
                              </Button>
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {isCheckInReviewStep && (
                <Card variant="outlined" data-testid="check-in-step-review">
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Преглед на AI промени
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Изчакваме check-in резултата, преди да разрешим старта на тренировката.
                      </Typography>
                      {adaptationLoading && (
                        <Stack spacing={1.5}>
                          <LinearProgress />
                          <Typography variant="body2" color="text.secondary">
                            Подготвяме AI адаптацията...
                          </Typography>
                        </Stack>
                      )}
                      {!adaptationLoading && adaptationResult && (
                        <Stack spacing={1}>
                          <Alert severity={adaptationResult.adaptationApplied ? 'success' : 'warning'}>
                            {adaptationResult.adaptationApplied
                              ? `AI адаптацията е готова (${adaptationResult.appliedChanges.length} промени).`
                              : (adaptationResult.message || 'Адаптацията не беше приложена.')}
                          </Alert>
                          {adaptationResult.appliedChanges.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Предложени промени
                              </Typography>
                              <List dense sx={{ py: 0 }}>
                                {adaptationResult.appliedChanges.map((change, index) => (
                                  <ListItem key={`applied-${index}`} disablePadding sx={{ py: 0.25 }}>
                                    <ListItemText
                                      primary={
                                        change.type === 'exercise_substitution'
                                          ? `${change.exerciseName} -> ${change.replacementExerciseName}`
                                          : (change.reason || change.type)
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          )}
                          {adaptationResult.rejectedChanges.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Неприложени промени
                              </Typography>
                              <List dense sx={{ py: 0 }}>
                                {adaptationResult.rejectedChanges.map((change, index) => (
                                  <ListItem key={`rejected-${index}`} disablePadding sx={{ py: 0.25 }}>
                                    <ListItemText
                                      primary={`${change.exerciseName || 'Упражнение'}: ${change.reason || 'Без алтернатива'}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {checkInErrors.length > 0 && (
                <Alert severity="error" data-testid="check-in-error">
                  {checkInErrors.join(' | ')}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeCheckInDialog}>
              Отказ
            </Button>
            {!isCheckInReviewStep && (
              <>
                <Button onClick={handleCheckInBack} disabled={checkInStepIndex === 0}>
                  Назад
                </Button>
                <Button onClick={handleCheckInNext} data-testid="check-in-next-button">
                  {checkInStepIndex === checkInSteps.length - 1 ? 'Преглед на AI промени' : 'Напред'}
                </Button>
              </>
            )}
            {isCheckInReviewStep && (
              <>
                <Button onClick={handleCheckInBack} disabled={adaptationLoading}>
                  Назад
                </Button>
                <Button
                  variant="contained"
                  onClick={handleStartAfterCheckIn}
                  disabled={adaptationLoading || !adaptationResult}
                  data-testid="check-in-start-button"
                >
                  Стартирай
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // If no workout type selected, show type selection screen
  if (!workoutType) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <FitnessIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
              Начни тренировка
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Избери какъв тип тренировка искаш да регистрираш днес
            </Typography>

            <Stack spacing={3}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => handleWorkoutTypeSelect('strength')}
                sx={{
                  py: 3,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                }}
              >
                💪 Силова тренировка
              </Button>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => handleWorkoutTypeSelect('cardio')}
                color="secondary"
                sx={{
                  py: 3,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                }}
              >
                🏃 Кардио сесия
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Render a single exercise block (header + sets)
  const renderExerciseBlock = (exerciseIndex: number) => {
    const log = exerciseLogs[exerciseIndex];
    if (!log) return null;
    const exercise = log.substitutedWith || log.programExercise.exercise;
    const showDetails = showDetailsMap[exerciseIndex] || false;
    const toggleDetails = () => setShowDetailsMap(prev => ({ ...prev, [exerciseIndex]: !prev[exerciseIndex] }));

    return (
      <Box key={exerciseIndex}>
        {/* Exercise Header */}
        <Card sx={{ mb: 2, opacity: log.skipped ? 0.5 : 1 }}>
          <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ mb: 0.5, wordBreak: 'break-word', pr: 0.5 }}
                >
                  {`${exerciseIndex + 1}. ${exercise.name}`}
                </Typography>
                {(() => {
                  const eq = exercise.equipmentEntity;
                  if (!eq || typeof eq !== 'object') return null;
                  const eqObj = eq as { id: number; make?: string | null; initialResistance?: string | number | null };
                  if (!sessionGymEquipmentIds.has(eqObj.id)) return null;
                  const hasMake = eqObj.make != null && eqObj.make !== '';
                  const hasRes = eqObj.initialResistance != null && eqObj.initialResistance !== '';
                  if (!hasMake && !hasRes) return null;
                  const parts: string[] = [];
                  if (hasMake) parts.push(eqObj.make!);
                  if (hasRes) parts.push(`начално натоварване: ${parseFloat(String(eqObj.initialResistance))} kg`);
                  return (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {parts.join(' · ')}
                    </Typography>
                  );
                })()}
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                  <Chip
                    label={exercise.muscleGroup}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {log.substitutedWith && (
                    <Chip label="Заменено" size="small" color="info" />
                  )}
                  {log.skipped && (
                    <Chip label="Пропуснато" size="small" color="warning" />
                  )}
                  {hasDetailContent(exercise) && (
                    <Chip
                      icon={<InfoIcon />}
                      label="инфо"
                      size="small"
                      color="info"
                      variant="outlined"
                      onClick={toggleDetails}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                  {!log.skipped && (
                    <Chip
                      label="Едностранно"
                      size="small"
                      color={exercise.unilateral || log.unilateralOverride ? 'secondary' : 'default'}
                      variant={exercise.unilateral || log.unilateralOverride ? 'filled' : 'outlined'}
                      onClick={exercise.unilateral ? undefined : () => handleToggleUnilateral(exerciseIndex)}
                      disabled={!!exercise.unilateral}
                      sx={{ cursor: exercise.unilateral ? 'default' : 'pointer' }}
                    />
                  )}
                </Stack>
              </Box>
              <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                {(exercise.hasVariations || exercise.parentExerciseId) && (
                  <IconButton
                    onClick={() => handleOpenVariationSwitcher(exerciseIndex)}
                    disabled={log.skipped}
                    size="large"
                    title="Смени вариация"
                    sx={{ color: 'primary.main' }}
                  >
                    <SwapIcon />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => handleOpenSubstitute(exerciseIndex)}
                  disabled={log.skipped}
                  size="large"
                  title="Замени упражнение"
                >
                  <SwapIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleToggleSkip(exerciseIndex)}
                  size="large"
                  color={log.skipped ? 'warning' : 'default'}
                  title="Пропусни упражнение"
                >
                  <SkipIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleRemoveExercise(exerciseIndex)}
                  size="large"
                  color="error"
                  disabled={exerciseLogs.length <= 1}
                  title="Премахни упражнение"
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Box>

            {/* Last workout info */}
            {log.lastWorkoutSummary && !log.skipped && (
              <Alert severity="info" icon={<FitnessIcon />} sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Последно: {log.lastWorkoutSummary}
                </Typography>
              </Alert>
            )}

            {/* Caution Notes */}
            {exercise.cautionNotes && !log.skipped && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  component="div"
                  sx={{ '& > :first-of-type': { mt: 0 }, '& > :last-child': { mb: 0 } }}
                  {...sanitizedHtmlProps(exercise.cautionNotes)}
                />
              </Alert>
            )}

            {/* Expandable details */}
            <Button
              onClick={toggleDetails}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
              sx={{ mt: 1 }}
            >
              {showDetails ? 'Скрий детайли' : 'Покажи детайли'}
            </Button>

            <Collapse in={showDetails}>
              <Box sx={{ mt: 2 }}>
                {/* Instructions Text */}
                {exercise.instructions && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Инструкции
                    </Typography>
                    <Box
                      sx={{
                        pl: 1,
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        '& > :first-of-type': { mt: 0 },
                        '& > :last-child': { mb: 0 },
                        '& ul, & ol': { pl: 3, my: 0.5 },
                        '& h3': { fontSize: '1rem', fontWeight: 600, my: 1 },
                        '& p': { my: 0.5 },
                      }}
                      {...sanitizedHtmlProps(exercise.instructions)}
                    />
                  </Box>
                )}

                {/* YouTube Video */}
                {exercise.youtubeVideoUrl && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Видео
                    </Typography>
                    <Box
                      sx={{
                        position: 'relative',
                        paddingBottom: '56.25%',
                        height: 0,
                        overflow: 'hidden',
                        borderRadius: 1,
                      }}
                    >
                      <iframe
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                        }}
                        src={(() => {
                          const url = exercise.youtubeVideoUrl!;
                          if (url.includes('/embed/')) return url;
                          const match = url.match(/(?:youtu\.be\/|[?&]v=)([^&\s]+)/);
                          return match ? `https://www.youtube.com/embed/${match[1]}` : url;
                        })()}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={`${exercise.name} video`}
                      />
                    </Box>
                  </Box>
                )}

                {/* Anatomy Images */}
                {exercise.anatomyImages && exercise.anatomyImages.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Анатомия
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                      {exercise.anatomyImages.map((imgUrl, idx) => (
                        <Box
                          key={idx}
                          component="img"
                          src={getImageUrl(imgUrl)}
                          alt={`Anatomy ${idx + 1}`}
                          onClick={() => openImageViewer(
                            exercise.anatomyImages!.map(u => getImageUrl(u)),
                            idx,
                            'Анатомия'
                          )}
                          sx={{
                            height: '160px',
                            width: 'auto',
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            '&:active': { opacity: 0.7 },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Instruction Images */}
                {exercise.instructionImages && exercise.instructionImages.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Техника
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                      {exercise.instructionImages.map((imgUrl, idx) => (
                        <Box
                          key={idx}
                          component="img"
                          src={getImageUrl(imgUrl)}
                          alt={`Instruction ${idx + 1}`}
                          onClick={() => openImageViewer(
                            exercise.instructionImages!.map(u => getImageUrl(u)),
                            idx,
                            'Техника'
                          )}
                          sx={{
                            height: '160px',
                            width: 'auto',
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            '&:active': { opacity: 0.7 },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Sets Input - Mobile Optimized */}
        {!log.skipped && (
          <Stack spacing={2}>
            {log.sets.map((set, setIndex) => {
              const setCompletionKey = getSetCompletionKey(exerciseIndex, setIndex);
              const isCompleted = isSetCompleted(set);
              const isExpandedCompleted = !!expandedCompletedSets[setCompletionKey];

              if (isCompleted && !isExpandedCompleted) {
                return (
                  <Paper
                    key={setIndex}
                    elevation={0}
                    data-testid={`completed-set-${exerciseIndex}-${setIndex}`}
                    sx={{
                      p: 1.25,
                      border: '1px solid',
                      borderColor: 'success.light',
                      bgcolor: (theme) => `${theme.palette.success.main}0f`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            Сет {set.setNumber} е готов
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {set.weight || 0} кг · {(exercise.unilateral || log.unilateralOverride)
                              ? `${set.repsLeft ?? 0}/${set.repsRight ?? 0}`
                              : set.reps} повт.
                          </Typography>
                        </Box>
                      </Stack>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => toggleCompletedSetExpanded(exerciseIndex, setIndex)}
                        sx={{ flexShrink: 0 }}
                      >
                        Редактирай
                      </Button>
                    </Box>
                  </Paper>
                );
              }

              return (
                <Paper
                  key={setIndex}
                  elevation={2}
                  data-testid={`workout-set-${exerciseIndex}-${setIndex}`}
                  sx={{
                    p: 2,
                    bgcolor: set.isDropSet ? 'warning.50' : 'background.paper',
                    borderLeft: set.isDropSet ? 4 : isCompleted ? 4 : 0,
                    borderLeftColor: set.isDropSet ? 'warning.main' : isCompleted ? 'success.main' : 'transparent',
                  }}
                >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: set.plannedReps !== undefined ? 1 : 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Сет {set.setNumber}
                    {set.isDropSet && (
                      <Chip label="Drop" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                    {isCompleted && (
                      <Chip label="Готов" size="small" color="success" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {isCompleted && (
                      <IconButton
                        onClick={() => toggleCompletedSetExpanded(exerciseIndex, setIndex)}
                        color="success"
                        size="small"
                        title="Скрий готовия сет"
                      >
                        <ExpandLessIcon />
                      </IconButton>
                    )}
                    {!set.isDropSet && (
                      <IconButton
                        onClick={() => handleAddDropSet(exerciseIndex, setIndex)}
                        color="primary"
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                    {(set.isDropSet || log.sets.length > 1) && (
                      <IconButton
                        onClick={() => handleRemoveSet(exerciseIndex, setIndex)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>
                </Box>

                {/* Planned targets indicator */}
                {set.plannedReps !== undefined && (
                  <Box
                    sx={{
                      mb: 2,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: (theme) => `${theme.palette.info.main}12`,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: (theme) => `${theme.palette.info.main}40`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <EventNoteIcon sx={{ fontSize: 16, color: 'info.main' }} />
                    <Typography variant="body2" sx={{ color: 'info.dark', fontWeight: 500 }}>
                      План: {set.plannedWeight != null && set.plannedWeight > 0 ? `${set.plannedWeight} кг × ` : ''}{set.plannedReps} повт.
                      {set.plannedRpe != null ? ` @ RPE ${set.plannedRpe}` : ''}
                    </Typography>
                  </Box>
                )}

                {/* Mobile-friendly input with +/- buttons */}
                <Stack spacing={2}>
                  {/* Weight Input */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Тежест (кг)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => adjustWeight(exerciseIndex, setIndex, -2.5)}
                        sx={{
                          minWidth: 48,
                          minHeight: 48,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRadius: '50%',
                          '&:hover': { bgcolor: 'primary.dark' },
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <TextField
                        type="text"
                        inputMode="decimal"
                        value={set.weight}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(\d)/, '$1');
                          handleSetChange(exerciseIndex, setIndex, 'weight', cleaned);
                        }}
                        onBlur={() => {
                          handleSetChange(exerciseIndex, setIndex, 'weight', parseFloat(String(set.weight)) || 0);
                        }}
                        inputProps={{
                          pattern: '[0-9.]*',
                          style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' },
                        }}
                        sx={{ flex: 1, '& input': { py: 1.5 } }}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => adjustWeight(exerciseIndex, setIndex, 2.5)}
                        sx={{
                          minWidth: 48,
                          minHeight: 48,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRadius: '50%',
                          '&:hover': { bgcolor: 'primary.dark' },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Reps Input */}
                  {(exercise.unilateral || log.unilateralOverride) ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Повторения (ляво / дясно)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* Left */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textAlign: 'center' }}>
                            Ляво
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleSetChange(exerciseIndex, setIndex, 'repsLeft', Math.max(0, (set.repsLeft ?? 0) - 1))}
                              sx={{
                                minWidth: 36,
                                minHeight: 36,
                                bgcolor: 'secondary.main',
                                color: 'secondary.contrastText',
                                borderRadius: '50%',
                                '&:hover': { bgcolor: 'secondary.dark' },
                              }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              type="text"
                              inputMode="numeric"
                              value={set.repsLeft ?? 0}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                                handleSetChange(exerciseIndex, setIndex, 'repsLeft', parseInt(cleaned) || 0);
                              }}
                              inputProps={{
                                pattern: '[0-9]*',
                                style: { textAlign: 'center', fontSize: '1.3rem', fontWeight: 'bold' },
                              }}
                              sx={{ flex: 1, '& input': { py: 1 } }}
                            />
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleSetChange(exerciseIndex, setIndex, 'repsLeft', (set.repsLeft ?? 0) + 1)}
                              sx={{
                                minWidth: 36,
                                minHeight: 36,
                                bgcolor: 'secondary.main',
                                color: 'secondary.contrastText',
                                borderRadius: '50%',
                                '&:hover': { bgcolor: 'secondary.dark' },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        {/* Right */}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textAlign: 'center' }}>
                            Дясно
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleSetChange(exerciseIndex, setIndex, 'repsRight', Math.max(0, (set.repsRight ?? 0) - 1))}
                              sx={{
                                minWidth: 36,
                                minHeight: 36,
                                bgcolor: 'secondary.main',
                                color: 'secondary.contrastText',
                                borderRadius: '50%',
                                '&:hover': { bgcolor: 'secondary.dark' },
                              }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              type="text"
                              inputMode="numeric"
                              value={set.repsRight ?? 0}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                                handleSetChange(exerciseIndex, setIndex, 'repsRight', parseInt(cleaned) || 0);
                              }}
                              inputProps={{
                                pattern: '[0-9]*',
                                style: { textAlign: 'center', fontSize: '1.3rem', fontWeight: 'bold' },
                              }}
                              sx={{ flex: 1, '& input': { py: 1 } }}
                            />
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleSetChange(exerciseIndex, setIndex, 'repsRight', (set.repsRight ?? 0) + 1)}
                              sx={{
                                minWidth: 36,
                                minHeight: 36,
                                bgcolor: 'secondary.main',
                                color: 'secondary.contrastText',
                                borderRadius: '50%',
                                '&:hover': { bgcolor: 'secondary.dark' },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Повторения
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => adjustReps(exerciseIndex, setIndex, -1)}
                          sx={{
                            minWidth: 48,
                            minHeight: 48,
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            borderRadius: '50%',
                            '&:hover': { bgcolor: 'secondary.dark' },
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          type="text"
                          inputMode="numeric"
                          value={set.reps}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                            handleSetChange(exerciseIndex, setIndex, 'reps', parseInt(cleaned) || 0);
                          }}
                          inputProps={{
                            pattern: '[0-9]*',
                            style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' },
                          }}
                          sx={{ flex: 1, '& input': { py: 1.5 } }}
                        />
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => adjustReps(exerciseIndex, setIndex, 1)}
                          sx={{
                            minWidth: 48,
                            minHeight: 48,
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            borderRadius: '50%',
                            '&:hover': { bgcolor: 'secondary.dark' },
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {/* RPE and Assistance in row */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl sx={{ flex: 1 }}>
                      <InputLabel>RPE</InputLabel>
                      <Select
                        value={set.rpe}
                        label="RPE"
                        onChange={(e) =>
                          handleSetChange(exerciseIndex, setIndex, 'rpe', e.target.value)
                        }
                        sx={{ '& .MuiSelect-select': { py: 1.5 } }}
                      >
                        {[5, 6, 7, 8, 9, 10].map((rpe) => (
                          <MenuItem key={rpe} value={rpe}>
                            {rpe} {rpe >= 9 ? '💪' : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={set.assistance}
                          onChange={(e) =>
                            handleSetChange(exerciseIndex, setIndex, 'assistance', e.target.checked)
                          }
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        />
                      }
                      label="Помощ"
                    />
                  </Box>

                  {/* Notes */}
                  <TextField
                    size="small"
                    placeholder="Бележки за серията..."
                    value={set.notes || ''}
                    onChange={(e) =>
                      handleSetChange(exerciseIndex, setIndex, 'notes', e.target.value)
                    }
                    fullWidth
                  />

                  <Button
                    variant={isCompleted ? 'outlined' : 'contained'}
                    color={isCompleted ? 'inherit' : 'success'}
                    fullWidth
                    startIcon={isCompleted ? <EditIcon /> : <CheckCircleIcon />}
                    onClick={() => handleSetCompletedChange(exerciseIndex, setIndex, !isCompleted)}
                    sx={{ py: 1.25 }}
                    data-testid={`set-complete-toggle-${exerciseIndex}-${setIndex}`}
                  >
                    {isCompleted ? 'Маркирай като неготов' : 'Готово'}
                  </Button>
                </Stack>
              </Paper>
              );
            })}

            {/* Add Set Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setExerciseLogs((prev) => {
                  const updated = [...prev];
                  const currentLog = updated[exerciseIndex];
                  const lastSet = currentLog.sets[currentLog.sets.length - 1];
                  const newSetNumber = currentLog.sets.filter(s => !s.isDropSet).length + 1;
                  const isUni = !!(exercise.unilateral || currentLog.unilateralOverride);
                  updated[exerciseIndex].sets.push({
                    setNumber: newSetNumber.toString(),
                    reps: lastSet.reps,
                    ...(isUni ? { repsLeft: lastSet.repsLeft ?? lastSet.reps, repsRight: lastSet.repsRight ?? lastSet.reps } : {}),
                    weight: lastSet.weight,
                    assistance: false,
                    rpe: 7,
                    isDropSet: false,
                  });
                  return updated;
                });
              }}
              sx={{ py: 1.5 }}
            >
              Добави сет
            </Button>

            {/* Muscle Group Body Map */}
            <ExerciseMuscleMap
              muscleGroup={exercise.muscleGroup}
              primaryMuscleGroup={exercise.primaryMuscleGroup}
              secondaryMuscleGroup={exercise.secondaryMuscleGroup}
              tertiaryMuscleGroup={exercise.tertiaryMuscleGroup}
            />
          </Stack>
        )}
      </Box>
    );
  };

  const renderWorkoutIntroOverlay = () => {
    if (!isIntroActive) {
      return null;
    }

    const disablePreferenceControl = (
      <FormControlLabel
        control={(
          <Checkbox
            checked={introDisableChecked}
            onChange={(e) => setIntroDisableChecked(e.target.checked)}
          />
        )}
        label="Не показвай отново"
        sx={{ mx: 0, color: 'common.white' }}
      />
    );

    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1400,
          px: 2,
          py: 3,
          bgcolor: 'rgba(11, 18, 32, 0.84)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 720,
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(180deg, rgba(23,37,84,0.96) 0%, rgba(15,23,42,0.98) 100%)',
            color: 'common.white',
            boxShadow: 24,
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Chip
                label={introPhase === 'countdown' ? 'Старт' : introPhase === 'warmup' ? 'Загрявка' : introPhase === 'interactive-ask' ? 'Режим' : introPhase === 'interactive-rest-pick' ? 'Почивка' : 'Потвърждение'}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'common.white' }}
              />
              <Button
                data-testid="workout-intro-skip"
                variant="text"
                color="inherit"
                onClick={() => finishWorkoutIntro(introDisableChecked)}
              >
                Пропусни
              </Button>
            </Stack>

            {introPhase === 'countdown' && (
              <Stack
                data-testid="workout-intro-countdown"
                spacing={3}
                alignItems="center"
                textAlign="center"
                sx={{ minHeight: 360, justifyContent: 'center' }}
              >
                <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.75 }}>
                  Подготви се за първата серия
                </Typography>
                <Typography sx={{ fontSize: { xs: '5rem', sm: '7rem' }, fontWeight: 800, lineHeight: 1 }}>
                  {WORKOUT_INTRO_COUNTDOWN_STEPS[introCountdownIndex]}
                </Typography>
                <Typography variant="h6" sx={{ maxWidth: 420, opacity: 0.82 }}>
                  Поеми въздух, подготви тежестите и започни тренировката с ясен ритъм.
                </Typography>
                {disablePreferenceControl}
              </Stack>
            )}

            {introPhase === 'warmup' && (
              <Stack data-testid="workout-intro-warmup" spacing={3}>
                <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                    Загрей Добре!
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.82 }}>
                    Мини през кратка загрявка, за да влезеш плавно в работните серии.
                  </Typography>
                </Box>

                {warmupPlanLoading && (
                  <Box>
                    <LinearProgress color="inherit" sx={{ borderRadius: 1, opacity: 0.5 }} />
                    <Typography variant="body2" sx={{ opacity: 0.6, mt: 1 }}>
                      Зарежда се план за загрявка…
                    </Typography>
                  </Box>
                )}

                {!warmupPlanLoading && (allWarmupImagesFailed) && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    Снимките за загрявка не са налични в момента. Продължи към подготовката без тях.
                  </Alert>
                )}

                {!warmupPlanLoading && currentWarmupSlide && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: warmupCheckedIndices.has(warmupGuideIndex)
                        ? 'rgba(76,175,80,0.12)'
                        : 'rgba(255,255,255,0.06)',
                      border: warmupCheckedIndices.has(warmupGuideIndex)
                        ? '1px solid rgba(76,175,80,0.4)'
                        : '1px solid rgba(255,255,255,0.12)',
                      transition: 'background-color 0.25s, border-color 0.25s',
                    }}
                  >
                    {/* Static image only shown for fallback slides */}
                    {!isDynamicWarmup && (currentWarmupSlide as WarmupGuideSlide & { imageUrl?: string | null }).imageUrl && (
                      <Box
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius: 2,
                          minHeight: { xs: 180, sm: 260 },
                          bgcolor: 'rgba(255,255,255,0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        {warmupImageErrors[warmupGuideIndex] ? (
                          <Typography variant="body1" sx={{ px: 3, textAlign: 'center', opacity: 0.82 }}>
                            Изображението за тази стъпка не можа да се зареди.
                          </Typography>
                        ) : (
                          <Box
                            component="img"
                            src={(currentWarmupSlide as WarmupGuideSlide & { imageUrl?: string | null }).imageUrl!}
                            alt={currentWarmupSlide.name}
                            onError={() => setWarmupImageErrors((prev) => ({ ...prev, [warmupGuideIndex]: true }))}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                      </Box>
                    )}

                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                          {currentWarmupSlide.name}
                        </Typography>

                        {/* reps / duration badge */}
                        {(currentWarmupSlide.reps !== undefined || currentWarmupSlide.durationSeconds !== undefined) && (
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            {currentWarmupSlide.reps !== undefined && (
                              <Chip
                                label={`${currentWarmupSlide.reps} повторения`}
                                size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'inherit' }}
                              />
                            )}
                            {currentWarmupSlide.durationSeconds !== undefined && (
                              <Chip
                                label={`${currentWarmupSlide.durationSeconds} сек`}
                                size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'inherit' }}
                              />
                            )}
                          </Stack>
                        )}

                        <Typography variant="body1" sx={{ opacity: 0.82 }}>
                          {currentWarmupSlide.description}
                        </Typography>
                      </Box>

                      {/* Mark as done checkbox */}
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={warmupCheckedIndices.has(warmupGuideIndex)}
                              onChange={(e) => {
                                setWarmupCheckedIndices((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) {
                                    next.add(warmupGuideIndex);
                                  } else {
                                    next.delete(warmupGuideIndex);
                                  }
                                  return next;
                                });
                              }}
                              sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#81c784' } }}
                            />
                          }
                          label={<Typography variant="caption" sx={{ opacity: 0.8 }}>Изпълнено</Typography>}
                          labelPlacement="bottom"
                          sx={{ mx: 0 }}
                        />
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {!warmupPlanLoading && (
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" flexWrap="wrap">
                    {effectiveWarmupSlides.map((slide, index) => (
                      <Button
                        key={`${slide.name}-${index}`}
                        variant={index === warmupGuideIndex ? 'contained' : 'outlined'}
                        color={warmupCheckedIndices.has(index) ? 'success' : 'inherit'}
                        size="small"
                        onClick={() => setWarmupGuideIndex(index)}
                        sx={{ minWidth: 36 }}
                      >
                        {warmupCheckedIndices.has(index) ? '✓' : index + 1}
                      </Button>
                    ))}
                  </Stack>
                )}

                {!warmupPlanLoading && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<PrevIcon />}
                      onClick={() => setWarmupGuideIndex((prev) => Math.max(0, prev - 1))}
                      disabled={warmupGuideIndex === 0}
                    >
                      Предишна
                    </Button>
                    <Button
                      variant="contained"
                      endIcon={<NextIcon />}
                      onClick={() => {
                        if (warmupGuideIndex >= effectiveWarmupSlides.length - 1 || effectiveWarmupSlides.length === 0) {
                          setIntroPhase('ready');
                          return;
                        }
                        setWarmupGuideIndex((prev) => Math.min(effectiveWarmupSlides.length - 1, prev + 1));
                      }}
                    >
                      {warmupGuideIndex >= effectiveWarmupSlides.length - 1 || effectiveWarmupSlides.length === 0 ? 'Към старта' : 'Следваща'}
                    </Button>
                  </Stack>
                )}

                {disablePreferenceControl}
              </Stack>
            )}

            {introPhase === 'ready' && (
              <Stack
                data-testid="workout-intro-ready"
                spacing={3}
                alignItems="center"
                textAlign="center"
                sx={{ minHeight: 360, justifyContent: 'center' }}
              >
                <Typography variant="h3" fontWeight={800}>
                  Готов ли си ?
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 440, opacity: 0.82 }}>
                  Влез в логера, когато си подредил тежестите и си готов за първата работна серия.
                </Typography>
                <Button
                  data-testid="workout-intro-confirm"
                  variant="contained"
                  size="large"
                  onClick={() => setIntroPhase('interactive-ask')}
                  sx={{ minWidth: 220, py: 1.5, fontSize: '1rem', fontWeight: 700 }}
                >
                  Разбира се
                </Button>
                {disablePreferenceControl}
              </Stack>
            )}

            {introPhase === 'interactive-ask' && (
              <Stack
                data-testid="workout-intro-interactive-ask"
                spacing={3}
                alignItems="center"
                textAlign="center"
                sx={{ minHeight: 360, justifyContent: 'center' }}
              >
                <Typography variant="h4" fontWeight={800}>
                  Интерактивен режим
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 480, opacity: 0.82 }}>
                  Водещ режим серия по серия с таймер за изпълнение и автоматична почивка. Перфектен за фокусирана тренировка.
                </Typography>
                <Stack spacing={2} sx={{ width: '100%', maxWidth: 320 }}>
                  <Button
                    data-testid="workout-intro-interactive-yes"
                    variant="contained"
                    size="large"
                    onClick={() => setIntroPhase('interactive-rest-pick')}
                    sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700 }}
                  >
                    Да, искам
                  </Button>
                  <Button
                    data-testid="workout-intro-interactive-no"
                    variant="outlined"
                    color="inherit"
                    size="large"
                    onClick={() => finishWorkoutIntro(introDisableChecked)}
                    sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700 }}
                  >
                    Не, класически
                  </Button>
                </Stack>
              </Stack>
            )}

            {introPhase === 'interactive-rest-pick' && (
              <Stack
                data-testid="workout-intro-rest-pick"
                spacing={3}
                alignItems="center"
                textAlign="center"
                sx={{ minHeight: 360, justifyContent: 'center' }}
              >
                <Typography variant="h4" fontWeight={800}>
                  Почивка между сериите
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 440, opacity: 0.82 }}>
                  Избери колко да почиваш между сериите. Може да пропуснеш почивката по всяко време.
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
                  {[
                    { label: '1 мин', seconds: 60 },
                    { label: '1:30', seconds: 90 },
                    { label: '2 мин', seconds: 120 },
                    { label: '3 мин', seconds: 180 },
                  ].map((opt) => (
                    <Button
                      key={opt.seconds}
                      variant="outlined"
                      color="inherit"
                      size="large"
                      onClick={() => {
                        setInteractiveRestSeconds(opt.seconds);
                        setInteractiveMode(true);
                        finishWorkoutIntro(introDisableChecked);
                      }}
                      sx={{
                        py: 2,
                        px: 3,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        minWidth: 100,
                        borderColor: 'rgba(255,255,255,0.3)',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.08)' },
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Strength training mode continues with original logic
  return (
    <>
      <Box ref={workoutContentRef} sx={{ pb: 12, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sticky Header with Progress */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button onClick={() => setSelectedDay(null)} size="small" sx={{ minWidth: 'auto', px: 1 }}>
              ← Назад
            </Button>
            <IconButton
              onClick={handleCancelWorkout}
              size="small"
              color="error"
              title="Откажи тренировка"
              sx={{ ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {workoutStartedAt && (
              <WorkoutTimer
                startedAt={workoutStartedAt}
                onTimerStateChange={setTimerState}
                initialState={timerState}
              />
            )}
            <IconButton
              onClick={() => setReorderDrawerOpen(true)}
              disabled={exerciseLogs.length < 2}
              size="small"
              title="Подреди упражненията"
              data-testid="reorder-exercises-button"
            >
              <ReorderIcon fontSize="small" />
            </IconButton>
            {interactiveMode ? (
              <Chip
                icon={<ListIcon />}
                label={`${currentStepIndex + 1} / ${steps.length}`}
                size="small"
                variant="outlined"
                onClick={() => setExerciseDrawerOpen(true)}
                data-testid="workout-step-counter"
                sx={{ cursor: 'pointer' }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" data-testid="workout-step-counter">
                {currentStepIndex + 1} / {steps.length}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={handleSaveWorkout}
            disabled={loading}
            size="small"
            color="success"
            startIcon={<CheckIcon />}
          >
            Запази
          </Button>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 6, borderRadius: 3, mb: 1 }}
        />

        <Typography variant="subtitle1" fontWeight="bold" noWrap>
          {dayNames[selectedDay] || `Ден ${selectedDay}`}
        </Typography>
      </Box>

      <Box sx={{ px: 2, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {currentSession && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventNoteIcon sx={{ color: 'info.main' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Допълнителна информация за тренировката
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                label="Допълнителна информация за тренировката"
                value={currentSession.notes ?? ''}
                onChange={(event) => handleSessionNotesChange(event.target.value)}
                inputProps={{ maxLength: WORKOUT_SESSION_NOTES_MAX_LENGTH }}
                helperText={`${(currentSession.notes ?? '').length}/${WORKOUT_SESSION_NOTES_MAX_LENGTH}`}
              />
            </CardContent>
          </Card>
        )}

        {currentStep && interactiveMode ? (
          /* ====== INTERACTIVE MODE RENDERING ====== */
          <Box>
            {(() => {
              const activeExIdx = currentStep.exerciseIndices[interactivePosition.exerciseOffset] ?? currentStep.exerciseIndices[0];
              const activeLog = exerciseLogs[activeExIdx];
              if (!activeLog) return null;
              const activeExercise = activeLog.substitutedWith || activeLog.programExercise.exercise;
              const activeSet = activeLog.sets[interactivePosition.setIndex];
              const totalSetsForExercise = activeLog.sets.length;

              if (interactiveSetPhase === 'exercise-summary') {
                /* ---- Exercise Summary Phase ---- */
                return (
                  <Stack spacing={2}>
                    {currentStep.isSuperSet && (
                      <Chip label="СУПЕРСЕТ" color="warning" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.9rem', alignSelf: 'flex-start' }} />
                    )}
                    {currentStep.exerciseIndices.map((exIdx) => {
                      const log = exerciseLogs[exIdx];
                      if (!log) return null;
                      const ex = log.substitutedWith || log.programExercise.exercise;
                      return (
                        <Card key={exIdx} sx={{ mb: 1 }}>
                          <CardContent>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                              {ex.name}
                            </Typography>
                            {log.sets.map((set, sIdx) => (
                              <Paper key={sIdx} elevation={1} sx={{ p: 1.5, mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    Сет {set.setNumber}
                                  </Typography>
                                  {set.durationSeconds !== undefined && (
                                    <Chip
                                      icon={<TimerIcon />}
                                      label={`${set.durationSeconds} сек`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                                {/* Weight with +/- */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => adjustWeight(exIdx, sIdx, -2.5)}
                                    sx={{
                                      minWidth: 36, minHeight: 36,
                                      bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%',
                                      '&:hover': { bgcolor: 'primary.dark' },
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    type="text"
                                    inputMode="decimal"
                                    size="small"
                                    value={set.weight}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(\d)/, '$1');
                                      handleSetChange(exIdx, sIdx, 'weight', cleaned);
                                    }}
                                    onBlur={() => handleSetChange(exIdx, sIdx, 'weight', parseFloat(String(set.weight)) || 0)}
                                    inputProps={{ pattern: '[0-9.]*', style: { textAlign: 'center', fontWeight: 'bold' } }}
                                    sx={{ flex: 1 }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">кг</InputAdornment> }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => adjustWeight(exIdx, sIdx, 2.5)}
                                    sx={{
                                      minWidth: 36, minHeight: 36,
                                      bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%',
                                      '&:hover': { bgcolor: 'primary.dark' },
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {/* Reps with +/- */}
                                {(ex.unilateral || log.unilateralOverride) ? (
                                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>Л</Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton size="small" color="secondary" onClick={() => handleSetChange(exIdx, sIdx, 'repsLeft', Math.max(0, (set.repsLeft ?? 0) - 1))}
                                          sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                          <RemoveIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <TextField type="text" inputMode="numeric" size="small" value={set.repsLeft ?? 0}
                                          onChange={(e) => { const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1'); handleSetChange(exIdx, sIdx, 'repsLeft', parseInt(cleaned) || 0); }}
                                          inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 'bold' } }} sx={{ flex: 1 }} />
                                        <IconButton size="small" color="secondary" onClick={() => handleSetChange(exIdx, sIdx, 'repsLeft', (set.repsLeft ?? 0) + 1)}
                                          sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                          <AddIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>Д</Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton size="small" color="secondary" onClick={() => handleSetChange(exIdx, sIdx, 'repsRight', Math.max(0, (set.repsRight ?? 0) - 1))}
                                          sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                          <RemoveIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <TextField type="text" inputMode="numeric" size="small" value={set.repsRight ?? 0}
                                          onChange={(e) => { const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1'); handleSetChange(exIdx, sIdx, 'repsRight', parseInt(cleaned) || 0); }}
                                          inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 'bold' } }} sx={{ flex: 1 }} />
                                        <IconButton size="small" color="secondary" onClick={() => handleSetChange(exIdx, sIdx, 'repsRight', (set.repsRight ?? 0) + 1)}
                                          sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                          <AddIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      onClick={() => adjustReps(exIdx, sIdx, -1)}
                                      sx={{
                                        minWidth: 36, minHeight: 36,
                                        bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                        '&:hover': { bgcolor: 'secondary.dark' },
                                      }}
                                    >
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <TextField
                                      type="text"
                                      inputMode="numeric"
                                      size="small"
                                      value={set.reps}
                                      onChange={(e) => {
                                        const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                                        handleSetChange(exIdx, sIdx, 'reps', parseInt(cleaned) || 0);
                                      }}
                                      inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 'bold' } }}
                                      sx={{ flex: 1 }}
                                      InputProps={{ endAdornment: <InputAdornment position="end">повт.</InputAdornment> }}
                                    />
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      onClick={() => adjustReps(exIdx, sIdx, 1)}
                                      sx={{
                                        minWidth: 36, minHeight: 36,
                                        bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                        '&:hover': { bgcolor: 'secondary.dark' },
                                      }}
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                                {/* RPE Select */}
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel>RPE</InputLabel>
                                  <Select
                                    value={set.rpe}
                                    label="RPE"
                                    onChange={(e) => handleSetChange(exIdx, sIdx, 'rpe', e.target.value)}
                                  >
                                    {[5, 6, 7, 8, 9, 10].map((rpe) => (
                                      <MenuItem key={rpe} value={rpe}>
                                        {rpe} {rpe >= 9 ? '\uD83D\uDCAA' : ''}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Paper>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    })}
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      endIcon={isLastExercise ? <CheckIcon /> : <NextIcon />}
                      onClick={isLastExercise ? handleSaveWorkout : handleInteractiveConfirmExercise}
                      disabled={loading}
                      sx={{ py: 2, fontSize: '1.1rem', fontWeight: 700 }}
                      color={isLastExercise ? 'success' : 'primary'}
                    >
                      {isLastExercise ? 'Запази тренировката' : 'Потвърди и продължи'}
                    </Button>
                  </Stack>
                );
              }

              if (interactiveSetPhase === 'resting') {
                /* ---- Rest Countdown Phase ---- */
                const restProgress = interactiveRestSeconds > 0
                  ? ((interactiveRestSeconds - interactiveRestRemaining) / interactiveRestSeconds) * 100
                  : 100;

                // Compute next exercise/set preview
                const { exerciseOffset, setIndex } = interactivePosition;
                const nextOffset = exerciseOffset + 1;
                let nextExName = activeExercise.name;
                let nextSetNum = setIndex + 1;
                if (nextOffset < currentStep.exerciseIndices.length) {
                  const nextExIdx = currentStep.exerciseIndices[nextOffset];
                  const nextLog = exerciseLogs[nextExIdx];
                  if (nextLog) {
                    nextExName = (nextLog.substitutedWith || nextLog.programExercise.exercise).name;
                    nextSetNum = setIndex + 1;
                  }
                } else {
                  // Wrapping around to first exercise, next set round
                  const firstExIdx = currentStep.exerciseIndices[0];
                  const firstLog = exerciseLogs[firstExIdx];
                  if (firstLog) {
                    nextExName = (firstLog.substitutedWith || firstLog.programExercise.exercise).name;
                  }
                  nextSetNum = setIndex + 2;
                }

                return (
                  <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
                    <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.7 }}>
                      Почивка
                    </Typography>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <MuiCircularProgress
                        variant="determinate"
                        value={restProgress}
                        size={160}
                        thickness={4}
                        sx={{ color: 'primary.main' }}
                      />
                      <Box
                        sx={{
                          top: 0, left: 0, bottom: 0, right: 0,
                          position: 'absolute',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Typography variant="h2" fontWeight={800} color="text.primary">
                          {formatTimerDisplay(interactiveRestRemaining)}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Collapsible edit card for just-completed set */}
                    {(() => {
                      const lc = interactiveLastCompletedRef.current;
                      if (!lc) return null;
                      const lcLog = exerciseLogs[lc.exerciseIndex];
                      if (!lcLog) return null;
                      const lcSet = lcLog.sets[lc.setIndex];
                      if (!lcSet) return null;
                      const lcEx = lcLog.substitutedWith || lcLog.programExercise.exercise;
                      return (
                        <Card sx={{ width: '100%' }}>
                          <ButtonBase
                            onClick={() => setInteractiveRestEditOpen((v) => !v)}
                            sx={{ width: '100%', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EditIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight="bold">
                                Редактирай сет {lc.setIndex + 1} — {lcEx.name}
                              </Typography>
                            </Box>
                            {interactiveRestEditOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </ButtonBase>
                          <Collapse in={interactiveRestEditOpen}>
                            <CardContent sx={{ pt: 0 }}>
                              {/* Weight */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => adjustWeight(lc.exerciseIndex, lc.setIndex, -2.5)}
                                  sx={{
                                    minWidth: 36, minHeight: 36,
                                    bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                  type="text"
                                  inputMode="decimal"
                                  size="small"
                                  value={lcSet.weight}
                                  onChange={(e) => {
                                    const cleaned = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(\d)/, '$1');
                                    handleSetChange(lc.exerciseIndex, lc.setIndex, 'weight', cleaned);
                                  }}
                                  onBlur={() => handleSetChange(lc.exerciseIndex, lc.setIndex, 'weight', parseFloat(String(lcSet.weight)) || 0)}
                                  inputProps={{ pattern: '[0-9.]*', style: { textAlign: 'center', fontWeight: 'bold' } }}
                                  sx={{ flex: 1 }}
                                  InputProps={{ endAdornment: <InputAdornment position="end">кг</InputAdornment> }}
                                />
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => adjustWeight(lc.exerciseIndex, lc.setIndex, 2.5)}
                                  sx={{
                                    minWidth: 36, minHeight: 36,
                                    bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {/* Reps */}
                              {(lcEx.unilateral || lcLog.unilateralOverride) ? (
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>Л</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <IconButton size="small" color="secondary" onClick={() => handleSetChange(lc.exerciseIndex, lc.setIndex, 'repsLeft', Math.max(0, (lcSet.repsLeft ?? 0) - 1))}
                                        sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                        <RemoveIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                      <TextField type="text" inputMode="numeric" size="small" value={lcSet.repsLeft ?? 0}
                                        onChange={(e) => { const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1'); handleSetChange(lc.exerciseIndex, lc.setIndex, 'repsLeft', parseInt(cleaned) || 0); }}
                                        inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 'bold' } }} sx={{ flex: 1 }} />
                                      <IconButton size="small" color="secondary" onClick={() => handleSetChange(lc.exerciseIndex, lc.setIndex, 'repsLeft', (lcSet.repsLeft ?? 0) + 1)}
                                        sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                        <AddIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>Д</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <IconButton size="small" color="secondary" onClick={() => handleSetChange(lc.exerciseIndex, lc.setIndex, 'repsRight', Math.max(0, (lcSet.repsRight ?? 0) - 1))}
                                        sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                        <RemoveIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                      <TextField type="text" inputMode="numeric" size="small" value={lcSet.repsRight ?? 0}
                                        onChange={(e) => { const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1'); handleSetChange(lc.exerciseIndex, lc.setIndex, 'repsRight', parseInt(cleaned) || 0); }}
                                        inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 'bold' } }} sx={{ flex: 1 }} />
                                      <IconButton size="small" color="secondary" onClick={() => handleSetChange(lc.exerciseIndex, lc.setIndex, 'repsRight', (lcSet.repsRight ?? 0) + 1)}
                                        sx={{ minWidth: 30, minHeight: 30, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%', '&:hover': { bgcolor: 'secondary.dark' } }}>
                                        <AddIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => adjustReps(lc.exerciseIndex, lc.setIndex, -1)}
                                    sx={{
                                      minWidth: 36, minHeight: 36,
                                      bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                      '&:hover': { bgcolor: 'secondary.dark' },
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    type="text"
                                    inputMode="numeric"
                                    size="small"
                                    value={lcSet.reps}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                                      handleSetChange(lc.exerciseIndex, lc.setIndex, 'reps', parseInt(cleaned) || 0);
                                    }}
                                    inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 'bold' } }}
                                    sx={{ flex: 1 }}
                                    InputProps={{ endAdornment: <InputAdornment position="end">повт.</InputAdornment> }}
                                  />
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => adjustReps(lc.exerciseIndex, lc.setIndex, 1)}
                                    sx={{
                                      minWidth: 36, minHeight: 36,
                                      bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                      '&:hover': { bgcolor: 'secondary.dark' },
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                              {/* RPE + Assistance */}
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <InputLabel>RPE</InputLabel>
                                  <Select
                                    value={lcSet.rpe}
                                    label="RPE"
                                    onChange={(e) => handleSetChange(lc.exerciseIndex, lc.setIndex, 'rpe', e.target.value)}
                                  >
                                    {[5, 6, 7, 8, 9, 10].map((rpe) => (
                                      <MenuItem key={rpe} value={rpe}>
                                        {rpe} {rpe >= 9 ? '\uD83D\uDCAA' : ''}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={lcSet.assistance}
                                      onChange={(e) => handleSetChange(lc.exerciseIndex, lc.setIndex, 'assistance', e.target.checked)}
                                    />
                                  }
                                  label="Помощ"
                                />
                              </Box>
                            </CardContent>
                          </Collapse>
                        </Card>
                      );
                    })()}

                    <Paper sx={{ p: 2, textAlign: 'center', width: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        Следващ:
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {nextExName} — Сет {nextSetNum}
                      </Typography>
                    </Paper>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<SkipIcon />}
                      onClick={handleInteractiveSkipRest}
                      sx={{ py: 1.5, minWidth: 200 }}
                    >
                      Пропусни почивка
                    </Button>
                  </Stack>
                );
              }

              /* ---- Ready / Executing Phase ---- */
              if (!activeSet) return null;

              return (
                <Stack spacing={2}>
                  {currentStep.isSuperSet && (
                    <Chip label="СУПЕРСЕТ" color="warning" sx={{ fontWeight: 'bold', fontSize: '0.9rem', alignSelf: 'flex-start' }} />
                  )}
                  <Card>
                    <CardContent>
                      {/* Exercise header with action buttons */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {activeExercise.name}
                          </Typography>
                          <Typography variant="h6" color="text.secondary">
                            Сет {interactivePosition.setIndex + 1} от {totalSetsForExercise}
                          </Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                          {(activeExercise.hasVariations || activeExercise.parentExerciseId) && (
                            <IconButton
                              onClick={() => handleOpenVariationSwitcher(activeExIdx)}
                              size="large"
                              title="Смени вариация"
                              sx={{ color: 'primary.main' }}
                            >
                              <SwapIcon />
                            </IconButton>
                          )}
                          <IconButton
                            onClick={() => handleOpenSubstitute(activeExIdx)}
                            size="large"
                            title="Замени упражнение"
                          >
                            <SwapIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleToggleSkip(activeExIdx)}
                            size="large"
                            color={activeLog.skipped ? 'warning' : 'default'}
                            title="Пропусни упражнение"
                          >
                            <SkipIcon />
                          </IconButton>
                        </Stack>
                      </Box>

                      {/* Unilateral toggle */}
                      <Chip
                        label="Едностранно"
                        size="small"
                        color={activeExercise.unilateral || activeLog.unilateralOverride ? 'secondary' : 'default'}
                        variant={activeExercise.unilateral || activeLog.unilateralOverride ? 'filled' : 'outlined'}
                        onClick={activeExercise.unilateral ? undefined : () => handleToggleUnilateral(activeExIdx)}
                        disabled={!!activeExercise.unilateral}
                        sx={{ cursor: activeExercise.unilateral ? 'default' : 'pointer', mb: 1, alignSelf: 'flex-start' }}
                      />

                      {/* Planned targets */}
                      {activeSet.plannedReps !== undefined && (
                        <Paper sx={{ p: 1.5, mb: 2, bgcolor: (theme) => `${theme.palette.info.main}12`, border: '1px solid', borderColor: (theme) => `${theme.palette.info.main}40` }}>
                          <Typography variant="body2" color="info.main">
                            План: {activeSet.plannedWeight ? `${activeSet.plannedWeight} кг` : ''} × {activeSet.plannedReps} повт.
                            {activeSet.plannedRpe ? ` @ RPE ${activeSet.plannedRpe}` : ''}
                          </Typography>
                        </Paper>
                      )}

                      {/* Weight Input with +/- buttons */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Тежест (кг)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => adjustWeight(activeExIdx, interactivePosition.setIndex, -2.5)}
                            disabled={interactiveSetPhase === 'executing'}
                            sx={{
                              minWidth: 48, minHeight: 48,
                              bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%',
                              '&:hover': { bgcolor: 'primary.dark' },
                              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <TextField
                            type="text"
                            inputMode="decimal"
                            value={activeSet.weight}
                            onChange={(e) => {
                              const cleaned = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(\d)/, '$1');
                              handleSetChange(activeExIdx, interactivePosition.setIndex, 'weight', cleaned);
                            }}
                            onBlur={() => {
                              handleSetChange(activeExIdx, interactivePosition.setIndex, 'weight', parseFloat(String(activeSet.weight)) || 0);
                            }}
                            inputProps={{ pattern: '[0-9.]*', style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' } }}
                            sx={{ flex: 1, '& input': { py: 1.5 } }}
                            disabled={interactiveSetPhase === 'executing'}
                          />
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => adjustWeight(activeExIdx, interactivePosition.setIndex, 2.5)}
                            disabled={interactiveSetPhase === 'executing'}
                            sx={{
                              minWidth: 48, minHeight: 48,
                              bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%',
                              '&:hover': { bgcolor: 'primary.dark' },
                              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Reps Input with +/- buttons */}
                      {(activeExercise.unilateral || activeLog.unilateralOverride) ? (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Повторения (ляво / дясно)
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            {/* Left */}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textAlign: 'center' }}>
                                Ляво
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleSetChange(activeExIdx, interactivePosition.setIndex, 'repsLeft', Math.max(0, (activeSet.repsLeft ?? 0) - 1))}
                                  disabled={interactiveSetPhase === 'executing'}
                                  sx={{
                                    minWidth: 36, minHeight: 36,
                                    bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                    '&:hover': { bgcolor: 'secondary.dark' },
                                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                  type="text"
                                  inputMode="numeric"
                                  value={activeSet.repsLeft ?? 0}
                                  onChange={(e) => {
                                    const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                                    handleSetChange(activeExIdx, interactivePosition.setIndex, 'repsLeft', parseInt(cleaned) || 0);
                                  }}
                                  inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontSize: '1.3rem', fontWeight: 'bold' } }}
                                  sx={{ flex: 1, '& input': { py: 1 } }}
                                  disabled={interactiveSetPhase === 'executing'}
                                />
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleSetChange(activeExIdx, interactivePosition.setIndex, 'repsLeft', (activeSet.repsLeft ?? 0) + 1)}
                                  disabled={interactiveSetPhase === 'executing'}
                                  sx={{
                                    minWidth: 36, minHeight: 36,
                                    bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                    '&:hover': { bgcolor: 'secondary.dark' },
                                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            {/* Right */}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', textAlign: 'center' }}>
                                Дясно
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleSetChange(activeExIdx, interactivePosition.setIndex, 'repsRight', Math.max(0, (activeSet.repsRight ?? 0) - 1))}
                                  disabled={interactiveSetPhase === 'executing'}
                                  sx={{
                                    minWidth: 36, minHeight: 36,
                                    bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                    '&:hover': { bgcolor: 'secondary.dark' },
                                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                  type="text"
                                  inputMode="numeric"
                                  value={activeSet.repsRight ?? 0}
                                  onChange={(e) => {
                                    const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                                    handleSetChange(activeExIdx, interactivePosition.setIndex, 'repsRight', parseInt(cleaned) || 0);
                                  }}
                                  inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontSize: '1.3rem', fontWeight: 'bold' } }}
                                  sx={{ flex: 1, '& input': { py: 1 } }}
                                  disabled={interactiveSetPhase === 'executing'}
                                />
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleSetChange(activeExIdx, interactivePosition.setIndex, 'repsRight', (activeSet.repsRight ?? 0) + 1)}
                                  disabled={interactiveSetPhase === 'executing'}
                                  sx={{
                                    minWidth: 36, minHeight: 36,
                                    bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                    '&:hover': { bgcolor: 'secondary.dark' },
                                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Повторения
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => adjustReps(activeExIdx, interactivePosition.setIndex, -1)}
                              disabled={interactiveSetPhase === 'executing'}
                              sx={{
                                minWidth: 48, minHeight: 48,
                                bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                '&:hover': { bgcolor: 'secondary.dark' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>
                            <TextField
                              type="text"
                              inputMode="numeric"
                              value={activeSet.reps}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(/[^0-9]/g, '').replace(/^0+(\d)/, '$1');
                                handleSetChange(activeExIdx, interactivePosition.setIndex, 'reps', parseInt(cleaned) || 0);
                              }}
                              inputProps={{ pattern: '[0-9]*', style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 'bold' } }}
                              sx={{ flex: 1, '& input': { py: 1.5 } }}
                              disabled={interactiveSetPhase === 'executing'}
                            />
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => adjustReps(activeExIdx, interactivePosition.setIndex, 1)}
                              disabled={interactiveSetPhase === 'executing'}
                              sx={{
                                minWidth: 48, minHeight: 48,
                                bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: '50%',
                                '&:hover': { bgcolor: 'secondary.dark' },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      )}

                      {/* RPE Select + Assistance */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                        <FormControl sx={{ flex: 1 }}>
                          <InputLabel>RPE</InputLabel>
                          <Select
                            value={activeSet.rpe}
                            label="RPE"
                            onChange={(e) =>
                              handleSetChange(activeExIdx, interactivePosition.setIndex, 'rpe', e.target.value)
                            }
                            disabled={interactiveSetPhase === 'executing'}
                            sx={{ '& .MuiSelect-select': { py: 1.5 } }}
                          >
                            {[5, 6, 7, 8, 9, 10].map((rpe) => (
                              <MenuItem key={rpe} value={rpe}>
                                {rpe} {rpe >= 9 ? '\uD83D\uDCAA' : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={activeSet.assistance}
                              onChange={(e) =>
                                handleSetChange(activeExIdx, interactivePosition.setIndex, 'assistance', e.target.checked)
                              }
                              disabled={interactiveSetPhase === 'executing'}
                              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                            />
                          }
                          label="Помощ"
                        />
                      </Box>

                      {interactiveSetPhase === 'executing' && (
                        /* Timer display while executing */
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <Typography
                            variant="h1"
                            fontWeight={800}
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              animation: 'pulse 2s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                              },
                            }}
                          >
                            {formatTimerDisplay(interactiveSetElapsed)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Времетраене на серията
                          </Typography>
                        </Box>
                      )}

                      {/* Action button */}
                      {interactiveSetPhase === 'ready' ? (
                        <>
                          <Button
                            variant="contained"
                            color="success"
                            size="large"
                            fullWidth
                            startIcon={<PlayIcon />}
                            onClick={handleInteractiveStart}
                            sx={{ py: 2, fontSize: '1.2rem', fontWeight: 700 }}
                          >
                            Старт
                          </Button>

                          {/* Quick Save */}
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<TimerIcon />}
                            onClick={() => setQuickSaveOpen((v) => !v)}
                            sx={{ mt: 1, alignSelf: 'center' }}
                          >
                            Бързо записване
                          </Button>
                          <Collapse in={quickSaveOpen}>
                            <Paper sx={{ p: 2, mt: 1 }}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                <TextField
                                  type="number"
                                  inputMode="numeric"
                                  size="small"
                                  label="мин"
                                  value={quickSaveMinutes}
                                  onChange={(e) => setQuickSaveMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                  inputProps={{ min: 0, max: 30, style: { textAlign: 'center' } }}
                                  sx={{ flex: 1 }}
                                />
                                <TextField
                                  type="number"
                                  inputMode="numeric"
                                  size="small"
                                  label="сек"
                                  value={quickSaveSeconds}
                                  onChange={(e) => setQuickSaveSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                                  inputProps={{ min: 0, max: 59, style: { textAlign: 'center' } }}
                                  sx={{ flex: 1 }}
                                />
                              </Box>
                              <Button
                                variant="contained"
                                fullWidth
                                startIcon={<SaveIcon />}
                                onClick={handleQuickSave}
                                disabled={quickSaveMinutes === 0 && quickSaveSeconds === 0}
                              >
                                Запиши
                              </Button>
                            </Paper>
                          </Collapse>
                        </>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          fullWidth
                          startIcon={<StopIcon />}
                          onClick={handleInteractiveDone}
                          sx={{ py: 2, fontSize: '1.2rem', fontWeight: 700 }}
                        >
                          Готово
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Stack>
              );
            })()}
          </Box>
        ) : currentStep && (
          /* ====== CLASSIC MODE RENDERING ====== */
          <Box>
            {/* Superset banner */}
            {currentStep.isSuperSet && (
              <Chip
                label="СУПЕРСЕТ"
                color="warning"
                sx={{ mb: 2, fontWeight: 'bold', fontSize: '0.9rem' }}
              />
            )}
            <Box sx={currentStep.isSuperSet ? {
              borderLeft: 4,
              borderLeftColor: 'warning.main',
              pl: 2,
            } : {}}>
              {currentExerciseIndices.map((idx, i) => (
                <React.Fragment key={idx}>
                  {i > 0 && currentStep.isSuperSet && (
                    <Box sx={{ my: 2, borderTop: 2, borderColor: 'warning.main', opacity: 0.5 }} />
                  )}
                  {renderExerciseBlock(idx)}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}

        {isLastExercise && (
          <Button
            data-testid="add-adhoc-exercise-button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenAddExercise}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            Добави adhoc упражнение
          </Button>
        )}

        {/* Rest Timer - hidden in interactive mode */}
        {!interactiveMode && <RestTimer />}
      </Box>

      {/* Bottom Navigation */}
      {interactiveMode ? (
        /* Interactive mode: hide bottom nav (action buttons are inline in the card) */
        null
      ) : (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: 2,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<PrevIcon />}
            onClick={goToPrevExercise}
            disabled={isFirstExercise}
            sx={{ flex: 1, py: 1.5 }}
          >
            {isMobile ? 'Назад' : 'Предишно'}
          </Button>
          {isLastExercise ? (
            <Button
              variant="contained"
              color="success"
              endIcon={<CheckIcon />}
              onClick={handleSaveWorkout}
              disabled={loading}
              sx={{ flex: 1, py: 1.5 }}
            >
              Запази
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={goToNextExercise}
              sx={{ flex: 1, py: 1.5 }}
            >
              Напред
            </Button>
          )}
        </Box>
      )}

      {/* Exercise Navigation Drawer (interactive mode) */}
      <Drawer
        anchor="bottom"
        open={exerciseDrawerOpen}
        onClose={() => setExerciseDrawerOpen(false)}
        PaperProps={{ sx: { maxHeight: '70vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <Box sx={{ width: 40, height: 4, bgcolor: 'grey.400', borderRadius: 2, mx: 'auto', mt: 1, mb: 0.5 }} />
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Упражнения
          </Typography>
          <Box sx={{ overflowY: 'auto', maxHeight: 'calc(70vh - 80px)' }}>
            <DndContext sensors={drawerSensors} collisionDetection={closestCenter} onDragEnd={handleExerciseDragEnd}>
              <SortableContext items={steps.map((s) => s.exerciseIndices[0])} strategy={verticalListSortingStrategy}>
                <List disablePadding>
                  {steps.map((step, stepIdx) => {
                    const firstExIdx = step.exerciseIndices[0];
                    const log = exerciseLogs[firstExIdx];
                    if (!log) return null;
                    const ex = log.substitutedWith || log.programExercise.exercise;
                    const totalSets = log.sets.length;
                    const doneSets = countCompletedSets(log.sets);
                    const isCurrent = stepIdx === currentStepIndex;
                    const isCompleted = doneSets === totalSets && totalSets > 0;
                    return (
                      <SortableExerciseItem
                        key={firstExIdx}
                        id={firstExIdx}
                        stepIdx={stepIdx}
                        exerciseName={ex.name}
                        totalSets={totalSets}
                        doneSets={doneSets}
                        isCurrent={isCurrent}
                        isCompleted={isCompleted}
                        onSelect={(idx) => {
                          setCurrentStepIndex(idx);
                          setInteractivePosition({ exerciseOffset: 0, setIndex: 0 });
                          setInteractiveSetPhase('ready');
                          setInteractiveSetStartedAt(null);
                          setInteractiveSetElapsed(0);
                          setQuickSaveOpen(false);
                          setExerciseDrawerOpen(false);
                        }}
                      />
                    );
                  })}
                </List>
              </SortableContext>
            </DndContext>
          </Box>
        </Box>
      </Drawer>

      {/* Reorder Exercises Drawer (order screen) */}
      <Drawer
        anchor="bottom"
        open={reorderDrawerOpen}
        onClose={() => setReorderDrawerOpen(false)}
        PaperProps={{ sx: { maxHeight: '80vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <Box sx={{ width: 40, height: 4, bgcolor: 'grey.400', borderRadius: 2, mx: 'auto', mt: 1, mb: 0.5 }} />
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
            Подреди упражненията
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Влачи упражненията, за да смениш реда им.
          </Typography>
          <Box sx={{ overflowY: 'auto', maxHeight: 'calc(80vh - 150px)' }}>
            <DndContext sensors={drawerSensors} collisionDetection={closestCenter} onDragEnd={handleExerciseDragEnd}>
              <SortableContext items={exerciseLogs.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                <List disablePadding>
                  {exerciseLogs.map((log, idx) => {
                    const ex = log.substitutedWith || log.programExercise.exercise;
                    const totalSets = log.sets.length;
                    const doneSets = countCompletedSets(log.sets);
                    const currentStep = steps[currentStepIndex];
                    const isCurrent = !!currentStep?.exerciseIndices.includes(idx);
                    const isCompleted = doneSets === totalSets && totalSets > 0;
                    return (
                      <SortableExerciseItem
                        key={idx}
                        id={idx}
                        stepIdx={idx}
                        exerciseName={ex.name}
                        totalSets={totalSets}
                        doneSets={doneSets}
                        isCurrent={isCurrent}
                        isCompleted={isCompleted}
                      />
                    );
                  })}
                </List>
              </SortableContext>
            </DndContext>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setReorderDrawerOpen(false)}
            sx={{ mt: 2 }}
            data-testid="reorder-done-button"
          >
            Готово
          </Button>
        </Box>
      </Drawer>

      {/* Substitute Exercise Dialog */}
      <Dialog
        open={substituteDialogOpen}
        onClose={() => setSubstituteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Замени упражнение</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Избери упражнение за замяна (еднократна промяна само за тази тренировка)
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              value={substituteSearch}
              onChange={(e) => setSubstituteSearch(e.target.value)}
              label="Търси по име"
              fullWidth
              inputProps={{ 'data-testid': 'substitute-search-input' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="substitute-muscle-group-filter-label">Мускулна група</InputLabel>
                <Select
                  labelId="substitute-muscle-group-filter-label"
                  label="Мускулна група"
                  value={substituteFilterMuscle}
                  onChange={(e) => setSubstituteFilterMuscle(String(e.target.value))}
                  SelectDisplayProps={{ 'data-testid': 'substitute-muscle-group-filter' } as any}
                >
                  <MenuItem value="">Всички</MenuItem>
                  {substituteMuscleGroups.map((muscleGroup) => (
                    <MenuItem key={muscleGroup} value={muscleGroup}>
                      {muscleGroup}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="substitute-equipment-filter-label">Оборудване</InputLabel>
                <Select
                  labelId="substitute-equipment-filter-label"
                  label="Оборудване"
                  value={substituteFilterEquipment}
                  onChange={(e) => setSubstituteFilterEquipment(String(e.target.value))}
                  SelectDisplayProps={{ 'data-testid': 'substitute-equipment-filter' } as any}
                >
                  <MenuItem value="">Всичко</MenuItem>
                  {substituteEquipmentOptions.map((equipment) => (
                    <MenuItem key={equipment} value={equipment}>
                      {equipment}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="substitute-movement-pattern-filter-label">Movement pattern</InputLabel>
                <Select
                  labelId="substitute-movement-pattern-filter-label"
                  label="Movement pattern"
                  value={substituteFilterMovementPattern}
                  onChange={(e) => setSubstituteFilterMovementPattern(String(e.target.value))}
                  SelectDisplayProps={{ 'data-testid': 'substitute-movement-pattern-filter' } as any}
                >
                  <MenuItem value="">Всички</MenuItem>
                  {substituteMovementPatterns.map((pattern) => (
                    <MenuItem key={pattern} value={pattern}>
                      {pattern}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {substituteLoading && <LinearProgress />}
            {orderedSubstituteExercises.length > 0 ? (
              <List
                dense
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 360, overflow: 'auto' }}
                data-testid="substitute-results-list"
              >
                {orderedSubstituteExercises.map((exercise) => {
                  const historyEntry = substituteHistoryMap.get(exercise.id);
                  const isSelected = selectedSubstitute?.id === exercise.id;

                  return (
                    <ListItem key={exercise.id} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => setSelectedSubstitute(exercise)}
                        data-testid={`substitute-result-${exercise.id}`}
                      >
                        <ListItemText
                          primary={exercise.name}
                          secondary={[
                            exercise.muscleGroup,
                            exercise.equipment || 'без оборудване',
                            exercise.movementPattern || 'без pattern',
                            historyEntry?.lastPerformedAt
                              ? `Последно: ${new Date(historyEntry.lastPerformedAt).toLocaleDateString('bg-BG')}`
                              : 'Без история',
                          ].join(' • ')}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ mb: 1 }}>Няма намерени упражнения</Typography>
                <Button onClick={resetSubstituteFilters}>Изчисти филтрите</Button>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubstituteDialogOpen(false)}>Откажи</Button>
          <Button
            onClick={handleConfirmSubstitute}
            variant="contained"
            disabled={!selectedSubstitute || loading}
          >
            Потвърди
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addExerciseDialogOpen}
        onClose={() => setAddExerciseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Добави ad-hoc упражнение</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Избери упражнение, което да добавиш в края на текущата тренировка.
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Избери упражнение</InputLabel>
            <Select
              value={selectedSubstitute?.id || ''}
              label="Избери упражнение"
              onChange={(e) => {
                const selectedId = Number(e.target.value);
                const exercise = availableExercises.find((ex) => ex.id === selectedId);
                setSelectedSubstitute(exercise || null);
              }}
            >
              {availableExercises.map((exercise) => (
                <MenuItem key={exercise.id} value={exercise.id}>
                  {exercise.name} - {exercise.category} ({exercise.muscleGroup})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddExerciseDialogOpen(false)}>Откажи</Button>
          <Button
            onClick={handleConfirmAddExercise}
            variant="contained"
            disabled={!selectedSubstitute || loading}
          >
            Добави упражнение
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Image Viewer */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'black',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: 'rgba(0,0,0,0.8)',
            zIndex: 1,
          }}
        >
          <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
            {imageViewerTitle} ({imageViewerIndex + 1}/{imageViewerImages.length})
          </Typography>
          <IconButton onClick={() => setImageViewerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Image area with swipe */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {imageViewerImages.length > 1 && (
            <IconButton
              onClick={handleImageViewerPrev}
              sx={{
                position: 'absolute',
                left: 8,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.15)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                zIndex: 1,
              }}
            >
              <PrevIcon />
            </IconButton>
          )}

          <Box
            component="img"
            src={imageViewerImages[imageViewerIndex]}
            alt={`${imageViewerTitle} ${imageViewerIndex + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />

          {imageViewerImages.length > 1 && (
            <IconButton
              onClick={handleImageViewerNext}
              sx={{
                position: 'absolute',
                right: 8,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.15)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                zIndex: 1,
              }}
            >
              <NextIcon />
            </IconButton>
          )}
        </Box>

        {/* Dot indicators */}
        {imageViewerImages.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              py: 2,
              bgcolor: 'rgba(0,0,0,0.8)',
            }}
          >
            {imageViewerImages.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setImageViewerIndex(idx)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: idx === imageViewerIndex ? 'white' : 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Box>
        )}
      </Dialog>
      {/* Unplanned Workout Confirmation Dialog */}
      <Dialog
        open={confirmUnplannedOpen}
        onClose={() => setConfirmUnplannedOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Без план</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Нямаш план за тази тренировка. Искаш ли да продължиш без план?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setConfirmUnplannedOpen(false);
              if (pendingDay !== null) {
                startWorkoutWithoutPlan(pendingDay);
              }
            }}
          >
            Продължи без план
          </Button>
          {onNavigateToPlanner && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setConfirmUnplannedOpen(false);
                onNavigateToPlanner();
              }}
            >
              Планирай първо
            </Button>
          )}
          <Button
            fullWidth
            onClick={() => setConfirmUnplannedOpen(false)}
          >
            Откажи
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion Summary Dialog */}
      <Dialog
        open={completionSummary !== null}
        onClose={handleCloseCompletionOutro}
        maxWidth="md"
        fullWidth
        data-testid="workout-completion-outro"
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', display: 'block', mx: 'auto', mb: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            {completionOutroStep === 'summary' ? 'Тренировка завършена!' : 'Възстановяване след тренировка'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {completionSummary && completionOutroStep === 'summary' && (
            <Stack spacing={2} sx={{ py: 1 }} data-testid="summary-step">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Продължителност:</Typography>
                <Typography variant="body1" fontWeight="bold" data-testid="summary-duration">
                  {completionSummary.durationMinutes} мин
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Активно време:</Typography>
                <Typography variant="body1" fontWeight="bold" data-testid="summary-active-time">
                  {completionSummary.activeMinutes} мин
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Паузи:</Typography>
                <Typography variant="body1" fontWeight="bold" data-testid="summary-pause-time">
                  {completionSummary.pauseMinutes} мин
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Упражнения:</Typography>
                <Typography variant="body1" fontWeight="bold" data-testid="summary-exercises">
                  {completionSummary.exerciseCount}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">Серии:</Typography>
                <Typography variant="body1" fontWeight="bold" data-testid="summary-sets">
                  {completionSummary.setCount}
                </Typography>
              </Box>
            </Stack>
          )}
          {completionSummary && completionOutroStep === 'stretching' && (
            <Stack spacing={2} sx={{ py: 1 }}>
              <Stack spacing={1} data-testid="stretching-step">
                <Typography variant="body1" fontWeight="bold">
                  Отдели 3-5 минути за разтягане, за да подпомогнеш възстановяването след натоварването.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Избери позиция и отвори изображението за по-голям преглед.
                </Typography>
              </Stack>

              {stretchingContent.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(3, minmax(240px, 1fr))' : 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 2,
                    overflowX: isMobile ? 'auto' : 'visible',
                    pb: isMobile ? 1 : 0,
                    scrollSnapType: isMobile ? 'x proximity' : 'none',
                  }}
                >
                  {stretchingContent.map((item, index) => (
                    <ButtonBase
                      key={`${item.displayOrder}-${item.title}`}
                      onClick={() => item.safeImageUrl && openImageViewer([item.safeImageUrl], 0, item.title)}
                      data-testid="stretch-image-item"
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper',
                        scrollSnapAlign: isMobile ? 'start' : 'unset',
                      }}
                    >
                      <Box
                        component="img"
                        src={item.safeImageUrl!}
                        alt={item.alt}
                        sx={{
                          width: '100%',
                          height: 180,
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {index + 1}. {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                    </ButtonBase>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">
                  Напомняне: направи кратко разтягане за гърди, гръб и задно бедро преди да приключиш.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {completionOutroStep === 'summary' ? (
            <Button
              variant="contained"
              fullWidth
              color="success"
              onClick={() => setCompletionOutroStep('stretching')}
              data-testid="outro-next-button"
            >
              Към разтягане
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              color="success"
              onClick={handleCloseCompletionOutro}
              data-testid="outro-finish-button"
            >
              Готово
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Variation Switcher Dialog */}
      <Dialog open={variationDialogOpen} onClose={() => setVariationDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Смени вариация</DialogTitle>
        <DialogContent>
          {loadingVariationOptions ? (
            <Typography sx={{ py: 2 }}>Зареждане...</Typography>
          ) : (variationOptions.length === 0 && alternativeOptions.length === 0) ? (
            <Typography sx={{ py: 2 }} color="text.secondary">Няма налични вариации или алтернативи.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {variationOptions.length > 0 && (
                <Typography variant="overline" color="text.secondary">Вариации</Typography>
              )}
              {variationOptions.map((v) => {
                const varLog = exerciseLogs[variationExerciseIndex];
                const varExercise = varLog?.substitutedWith || varLog?.programExercise.exercise;
                const isCurrent = v.id === varExercise?.id;
                return (
                  <Card
                    key={`var-${v.id}`}
                    sx={{
                      cursor: isCurrent ? 'default' : 'pointer',
                      '&:hover': isCurrent ? {} : { backgroundColor: 'action.hover' },
                      border: isCurrent ? '2px solid' : '1px solid',
                      borderColor: isCurrent ? 'primary.main' : 'divider',
                      backgroundColor: isCurrent ? 'action.selected' : undefined,
                    }}
                    onClick={() => !isCurrent && handleSelectVariation(v)}
                  >
                    <CardContent sx={{ p: '12px !important', '&:last-child': { pb: '12px !important' } }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {v.variationName || v.name}
                        {isCurrent && ' (текущо)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.equipment} | {v.difficulty}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}

              {alternativeOptions.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="overline" color="text.secondary">
                    Алтернативи (заменящи упражнения)
                  </Typography>
                  {alternativeOptions.map((a) => {
                    const varLog = exerciseLogs[variationExerciseIndex];
                    const varExercise = varLog?.substitutedWith || varLog?.programExercise.exercise;
                    const isCurrent = a.id === varExercise?.id;
                    return (
                      <Card
                        key={`alt-${a.id}`}
                        sx={{
                          cursor: isCurrent ? 'default' : 'pointer',
                          '&:hover': isCurrent ? {} : { backgroundColor: 'action.hover' },
                          border: isCurrent ? '2px solid' : '1px solid',
                          borderColor: isCurrent ? 'secondary.main' : 'divider',
                          backgroundColor: isCurrent ? 'action.selected' : undefined,
                        }}
                        onClick={() => !isCurrent && handleSelectVariation(a)}
                      >
                        <CardContent sx={{ p: '12px !important', '&:last-child': { pb: '12px !important' } }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {a.name}
                            {isCurrent && ' (текущо)'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.equipment} | {a.difficulty}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVariationDialogOpen(false)}>Затвори</Button>
        </DialogActions>
      </Dialog>
      </Box>
      {renderWorkoutIntroOverlay()}
    </>
  );
};

export default WorkoutLogger;
